import type {
  CanonicalTreeRelationshipCreationDisposition,
} from '@/governor/tree/CanonicalTreeRelationshipCreationPolicy';
import { cleanParentIds } from '@/lib/graphIdSafety';
import { emitGovernorLiveAction } from '@/lib/governorEventBus';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';

import { supabase } from '@/lib/supabase';
import {
  gatewayInsertMember,
  gatewayDeleteMember,
  gatewayRollbackCreatedMember,
  gatewayRestoreMember,
  gatewaySetParents,
  gatewayConnectExistingAncestryAtomic,
  gatewaySetSpouse,
  gatewayUpdateMember,
  type GatewayConnectExistingAncestryRequest,
  type GatewayConnectExistingAncestryResult,
  type GatewayDeleteMemberRequest,
} from '@/lib/graph/graphMutationGateway';
import { useAuth } from '@/contexts/AuthContext';
import {
  notifyFamilyOfMemoryUpload,
  notifyFamilyOfMemberJoined,
} from '@/lib/emailNotifications';
import {
  loadCanonicalTreeExternalEvidenceSnapshot,
} from '@/governor/tree/CanonicalTreeExternalEvidenceSnapshotLoader';
import {
  adoptCanonicalTreeExternalEvidenceSnapshot,
  createCanonicalTreeExternalEvidenceContextState,
  invalidateCanonicalTreeExternalEvidenceContextState,
  type CanonicalTreeExternalEvidenceContextState,
} from '@/governor/tree/CanonicalTreeExternalEvidenceContextAdoptionSimulation';
import {
  summarizeCanonicalTreeExternalEvidenceContext,
} from '@/governor/tree/CanonicalTreeExternalEvidenceObservability';
import {
  getCanonicalTreeTabScopedSanitizedEvidenceRegistry,
} from '@/governor/tree/CanonicalTreeTabScopedSanitizedEvidenceRegistry';
import {
  enforceTreeRuntimeEffectiveGraphHydrationBoundary,
} from '@/governor/tree-runtime/TreeRuntimeEffectiveGraphHydrationBoundary';
import {
  mapTreeRuntimeGraphMemberRow,
} from '@/governor/tree-runtime/TreeRuntimeGraphMemberRowMapper';

export type Side = 'mother' | 'father' | 'self' | 'both';
export type ApprovalStatus = 'pending' | 'approved' | 'rejected';
export type LivingStatus = 'living' | 'deceased' | null;

export type Relationship =
  | 'mother'
  | 'father'
  | 'child'
  | 'sibling'
  | 'spouse'
  | 'co-parent'
  | 'grandparent'
  | 'great-grandparent'
  | 'aunt-uncle'
  | 'cousin'
  | 'cousin-child'
  | 'step-parent'
  | 'adopted-parent'
  | 'unknown';

export const RELATIONSHIP_OPTIONS: { value: Relationship; label: string }[] = [
  { value: 'mother', label: 'Mother' },
  { value: 'father', label: 'Father' },
  { value: 'child', label: 'Child' },
  { value: 'sibling', label: 'Sibling' },
  { value: 'spouse', label: 'Spouse / Partner' },
  { value: 'co-parent', label: 'Co-Parent' },
  { value: 'grandparent', label: 'Grandparent' },
  { value: 'great-grandparent', label: 'Great-Grandparent' },
  { value: 'aunt-uncle', label: 'Aunt / Uncle' },
  { value: 'cousin', label: 'Cousin' },
  { value: 'cousin-child', label: "Cousin's Child" },
  { value: 'step-parent', label: 'Step-Parent' },
  { value: 'adopted-parent', label: 'Adopted Parent' },
  { value: 'unknown', label: 'Unknown / Possible Relation' },
];


/**
 * EXTENDED relationship options — the SHARED source of truth used by BOTH
 * the quick-add menu (FamilyTreeView's AddRelativePopover) AND the
 * Add Family Member modal's relationship dropdown. Includes every
 * advanced label (Grandchild, Great-Grandchild, Great-Aunt/Uncle,
 * Niece/Nephew, 2nd Cousin, Step-relationships, In-laws, etc.) so the
 * label the user picked from the quick-add menu is preserved verbatim
 * when the modal opens.
 *
 * Each entry maps to:
 *   - `value`:               unique key (core Relationship enum value
 *                            OR a virtual key prefixed with `v_`).
 *   - `label`:               human-readable display label.
 *   - `coreRelationship`:    the underlying Relationship enum used for
 *                            parent_ids / connector / placement logic.
 *                            Virtual entries map to the closest-logical
 *                            core type so the kinship engine still
 *                            derives placement correctly.
 *   - `displayOverride`:     when set, this string is written to the
 *                            `relationship_override` column on save so
 *                            the kinship engine returns it verbatim
 *                            (e.g. "Great-Aunt / Uncle" while structurally
 *                            stored as 'aunt-uncle' — placement under the
 *                            grandparent column is automatic via parent_ids).
 *   - `defaultSide`:         optional side hint (e.g. step-mom → mother).
 *
 * IMPORTANT — Pure UI additive. The database `Relationship` enum and the
 * kinship engine are unchanged. Advanced labels are stored as the closest
 * core type + a `relationship_override` display string. parent_ids logic,
 * tree placement, and the kinship engine are NEVER modified.
 */
export interface ExtendedRelationshipOption {
  value: string;
  label: string;
  coreRelationship: Relationship;
  displayOverride?: string;
  defaultSide?: Side;
  /** Group label for visual sectioning in the dropdown. */
  group: 'core' | 'extended' | 'in-law' | 'step' | 'other';
  workflowKind?: TreeWorkflowKind;
  partnershipStatus?: TreePartnershipStatus;
  createsAncestry?: boolean;
  inheritsGeneration?: boolean;
  inheritsBranch?: boolean;
  creationDisposition?: CanonicalTreeRelationshipCreationDisposition;
}

export const EXTENDED_RELATIONSHIP_OPTIONS: ExtendedRelationshipOption[] = [
  // ---- Core relationships (mirror RELATIONSHIP_OPTIONS) ----
  { value: 'mother', label: 'Mother', coreRelationship: 'mother', defaultSide: 'mother', group: 'core' },
  { value: 'father', label: 'Father', coreRelationship: 'father', defaultSide: 'father', group: 'core' },
  { value: 'child', label: 'Child', coreRelationship: 'child', defaultSide: 'self', group: 'core' },
  { value: 'sibling', label: 'Sibling', coreRelationship: 'sibling', group: 'core' },
  { value: 'spouse', label: 'Spouse / Partner', coreRelationship: 'spouse', defaultSide: 'self', group: 'core' },
  { value: 'grandparent', label: 'Grandparent', coreRelationship: 'grandparent', group: 'core' },
  { value: 'aunt-uncle', label: 'Aunt / Uncle', coreRelationship: 'aunt-uncle', group: 'core' },
  { value: 'cousin', label: 'Cousin', coreRelationship: 'cousin', group: 'core' },
  { value: 'cousin-child', label: "Cousin's Child", coreRelationship: 'cousin-child', group: 'core' },

  // ---- Extended great/grand chain ----
  { value: 'great-grandparent', label: 'Great-Grandparent', coreRelationship: 'great-grandparent', group: 'extended' },
  { value: 'v_great_great_grandparent', label: 'Great-Great-Grandparent', coreRelationship: 'great-grandparent', displayOverride: 'Great-Great-Grandparent', group: 'extended' },
  { value: 'v_earlier_ancestor', label: 'Earlier Ancestor (5+ generations)', coreRelationship: 'great-grandparent', group: 'extended', createsAncestry: true },
  { value: 'v_grandchild', label: 'Grandchild', coreRelationship: 'child', displayOverride: 'Grandchild', defaultSide: 'self', group: 'extended' },
  { value: 'v_great_grandchild', label: 'Great-Grandchild', coreRelationship: 'child', displayOverride: 'Great-Grandchild', defaultSide: 'self', group: 'extended' },
  { value: 'v_great_aunt_uncle', label: 'Great-Aunt / Uncle', coreRelationship: 'aunt-uncle', displayOverride: 'Great-Aunt / Uncle', group: 'extended' },
  { value: 'v_great_great_aunt_uncle', label: 'Great-Great-Aunt / Uncle', coreRelationship: 'aunt-uncle', displayOverride: 'Great-Great-Aunt / Uncle', group: 'extended' },
  { value: 'v_niece_nephew', label: 'Niece / Nephew', coreRelationship: 'child', displayOverride: 'Niece / Nephew', group: 'extended' },
  { value: 'v_great_niece_nephew', label: 'Great-Niece / Nephew', coreRelationship: 'cousin-child', displayOverride: 'Great-Niece / Nephew', group: 'extended' },
  { value: 'v_2nd_cousin', label: '2nd Cousin', coreRelationship: 'cousin', displayOverride: 'Second Cousin', group: 'extended', creationDisposition: 'DERIVED_ONLY' },
  { value: 'v_3rd_cousin', label: '3rd Cousin', coreRelationship: 'cousin', displayOverride: 'Third Cousin', group: 'extended', creationDisposition: 'DERIVED_ONLY' },
  { value: 'v_cousin_once_removed', label: 'Cousin Once Removed', coreRelationship: 'cousin', displayOverride: 'Cousin Once Removed', group: 'extended', creationDisposition: 'DERIVED_ONLY' },

  // ---- Step relationships ----
  { value: 'step-parent', label: 'Step-Parent', coreRelationship: 'step-parent', group: 'step' },
  { value: 'v_step_mom', label: 'Step-Mom', coreRelationship: 'step-parent', displayOverride: 'Step-Mom', defaultSide: 'mother', group: 'step' },
  { value: 'v_step_dad', label: 'Step-Dad', coreRelationship: 'step-parent', displayOverride: 'Step-Dad', defaultSide: 'father', group: 'step' },
  { value: 'adopted-parent', label: 'Adopted Parent', coreRelationship: 'adopted-parent', group: 'step', workflowKind: 'structural', createsAncestry: true },
  { value: 'v_step_sibling', label: 'Step-Sibling', coreRelationship: 'sibling', displayOverride: 'Step-Sibling', group: 'step' },
  { value: 'v_step_child', label: 'Step-Child', coreRelationship: 'child', displayOverride: 'Step-Child', defaultSide: 'self', group: 'step' },
  { value: 'v_half_sibling', label: 'Half-Sibling', coreRelationship: 'sibling', displayOverride: 'Half-Sibling', group: 'step', workflowKind: 'structural', createsAncestry: true },
  // ---- In-laws ----
  // Mapped to coreRelationship='spouse' so the auto-connect-on-create
  // flow links them via spouse_id to a chosen blood relative (e.g. a
  // son-in-law is the SPOUSE of a child of yours). They appear BESIDE
  // their partner on the same tier — never as a child of yours — and
  // never inherit your bloodline parent_ids. The displayOverride
  // preserves the in-law title verbatim ("Son-in-law", etc.).
  // Mother/Father-in-law remain coreRelationship='unknown' for now
  // because they are parents-of-your-spouse (a different structural
  // pattern); they continue to use the existing free-form path.
  { value: 'v_mother_in_law', label: 'Mother-in-law', coreRelationship: 'unknown', displayOverride: 'Mother-in-law', defaultSide: 'self', group: 'in-law', creationDisposition: 'REQUIRES_TWO_ENDPOINTS' },
  { value: 'v_father_in_law', label: 'Father-in-law', coreRelationship: 'unknown', displayOverride: 'Father-in-law', defaultSide: 'self', group: 'in-law', creationDisposition: 'REQUIRES_TWO_ENDPOINTS' },
  { value: 'v_brother_in_law', label: 'Brother-in-law', coreRelationship: 'spouse', displayOverride: 'Brother-in-law', defaultSide: 'self', group: 'in-law' },
  { value: 'v_sister_in_law', label: 'Sister-in-law', coreRelationship: 'spouse', displayOverride: 'Sister-in-law', defaultSide: 'self', group: 'in-law' },
  { value: 'v_son_in_law', label: 'Son-in-law', coreRelationship: 'spouse', displayOverride: 'Son-in-law', defaultSide: 'self', group: 'in-law' },
  { value: 'v_daughter_in_law', label: 'Daughter-in-law', coreRelationship: 'spouse', displayOverride: 'Daughter-in-law', defaultSide: 'self', group: 'in-law' },

  // ---- Other / chosen family ----
  { value: 'v_former_spouse', label: 'Former Spouse', coreRelationship: 'unknown', displayOverride: 'Former Spouse', defaultSide: 'self', group: 'other', workflowKind: 'former-spouse-partner', partnershipStatus: 'former', createsAncestry: false, inheritsGeneration: true, inheritsBranch: true },
  { value: 'v_former_partner', label: 'Former Partner', coreRelationship: 'unknown', displayOverride: 'Former Partner', defaultSide: 'self', group: 'other', workflowKind: 'former-spouse-partner', partnershipStatus: 'former', createsAncestry: false, inheritsGeneration: true, inheritsBranch: true },
  { value: 'v_godparent', label: 'Godparent', coreRelationship: 'unknown', displayOverride: 'Godparent', defaultSide: 'self', group: 'other', workflowKind: 'god-family', createsAncestry: false, inheritsGeneration: true, inheritsBranch: false },
  { value: 'v_godmother', label: 'Godmother', coreRelationship: 'unknown', displayOverride: 'Godmother', defaultSide: 'self', group: 'other', workflowKind: 'god-family', createsAncestry: false, inheritsGeneration: true, inheritsBranch: false },
  { value: 'v_godfather', label: 'Godfather', coreRelationship: 'unknown', displayOverride: 'Godfather', defaultSide: 'self', group: 'other', workflowKind: 'god-family', createsAncestry: false, inheritsGeneration: true, inheritsBranch: false },
  { value: 'v_godchild', label: 'Godchild', coreRelationship: 'unknown', displayOverride: 'Godchild', defaultSide: 'self', group: 'other', workflowKind: 'god-family', createsAncestry: false, inheritsGeneration: true, inheritsBranch: false },
  { value: 'v_chosen_parent', label: 'Chosen Parent', coreRelationship: 'unknown', displayOverride: 'Chosen Parent', defaultSide: 'self', group: 'other', workflowKind: 'chosen-family', createsAncestry: false, inheritsGeneration: true, inheritsBranch: false },
  { value: 'v_chosen_child', label: 'Chosen Child', coreRelationship: 'unknown', displayOverride: 'Chosen Child', defaultSide: 'self', group: 'other', workflowKind: 'chosen-family', createsAncestry: false, inheritsGeneration: true, inheritsBranch: false },
  { value: 'v_chosen_sibling', label: 'Chosen Sibling', coreRelationship: 'unknown', displayOverride: 'Chosen Sibling', defaultSide: 'self', group: 'other', workflowKind: 'chosen-family', createsAncestry: false, inheritsGeneration: true, inheritsBranch: false },
  { value: 'v_chosen_aunt_uncle', label: 'Chosen Aunt / Uncle', coreRelationship: 'unknown', displayOverride: 'Chosen Aunt / Uncle', defaultSide: 'self', group: 'other', workflowKind: 'chosen-family', createsAncestry: false, inheritsGeneration: true, inheritsBranch: false },
  { value: 'v_family_friend', label: 'Family Friend', coreRelationship: 'unknown', displayOverride: 'Family Friend', defaultSide: 'self', group: 'other', workflowKind: 'chosen-family', createsAncestry: false, inheritsGeneration: false, inheritsBranch: false },
  { value: 'v_coparent', label: 'Co-Parent', coreRelationship: 'co-parent', displayOverride: 'Co-Parent', defaultSide: 'self', group: 'other', workflowKind: 'co-parent', partnershipStatus: 'coparent-only', createsAncestry: false, inheritsGeneration: true, inheritsBranch: true },
  { value: 'v_guardian', label: 'Guardian', coreRelationship: 'unknown', displayOverride: 'Guardian', defaultSide: 'self', group: 'other', workflowKind: 'guardian', createsAncestry: false, inheritsGeneration: true, inheritsBranch: false },
  { value: 'v_foster_family', label: 'Foster Family', coreRelationship: 'unknown', displayOverride: 'Foster Family', defaultSide: 'self', group: 'other', workflowKind: 'foster-family', createsAncestry: false, inheritsGeneration: true, inheritsBranch: false },
  { value: 'v_affiliated_family', label: 'Affiliated Family', coreRelationship: 'unknown', displayOverride: 'Affiliated Family', defaultSide: 'self', group: 'other', workflowKind: 'affiliated-family', createsAncestry: false, inheritsGeneration: false, inheritsBranch: false },
  { value: 'unknown', label: 'Unknown / Possible Relation', coreRelationship: 'unknown', group: 'other', workflowKind: 'display-only', createsAncestry: false, inheritsGeneration: false, inheritsBranch: false },
];


export const buildRelationshipWorkflow = (
  option: ExtendedRelationshipOption,
  connectorMemberId?: string | null,
  secondaryConnectorMemberId?: string | null,
): TreeRelationshipWorkflow => {
  const value = option.value.toLowerCase();
  const core = option.coreRelationship;

  const kind: TreeWorkflowKind =
    option.workflowKind ??
    (option.group === 'in-law'
      ? 'in-law'
      : option.group === 'step'
        ? 'step-family'
        : value.includes('god')
          ? 'god-family'
          : value.includes('family_friend') ||
              value.includes('chosen')
            ? 'chosen-family'
            : value.includes('coparent') ||
                value.includes('co-parent')
              ? 'co-parent'
              : core === 'spouse'
                ? 'spouse-partner'
                : 'structural');

  const explicitLateralStructuralWorkflow =
    kind === 'structural' &&
    Boolean(connectorMemberId) &&
    core === 'aunt-uncle';

  const createsAncestry =
    option.createsAncestry ??
    (
      !explicitLateralStructuralWorkflow &&
      kind === 'structural' &&
      (
        core === 'mother' ||
        core === 'father' ||
        core === 'child' ||
        core === 'sibling' ||
        core === 'grandparent' ||
        core === 'great-grandparent' ||
        core === 'aunt-uncle' ||
        core === 'cousin' ||
        core === 'cousin-child' ||
        core === 'adopted-parent'
      )
    );

  const inheritsGeneration =
    option.inheritsGeneration ??
    kind !== 'display-only';

  const inheritsBranch =
    option.inheritsBranch ??
    (
      kind !== 'god-family' &&
      kind !== 'chosen-family' &&
      kind !== 'display-only'
    );

  const partnershipStatus: TreePartnershipStatus =
    option.partnershipStatus ??
    (kind === 'spouse-partner' || kind === 'in-law'
      ? 'current'
      : kind === 'co-parent'
        ? 'coparent-only'
        : 'not-applicable');

  return {
    kind,
    connectorMemberId: connectorMemberId ?? null,
    secondaryConnectorMemberId:
      secondaryConnectorMemberId ?? null,
    partnershipStatus,
    createsAncestry,
    inheritsGeneration,
    inheritsBranch,
    preferredFamilyTitle:
      option.displayOverride ?? option.label,
  };
};

/**
 * Resolve an extended-options entry by either:
 *   1. an exact `value` match (core enum value or virtual key), OR
 *   2. a `displayOverride` string match (case-insensitive trim) — used
 *      when the quick-add menu passed a customLabel like "Grandchild"
 *      that should map to the matching virtual entry rather than a
 *      free-form custom label.
 *
 * Returns null when neither matches — caller can treat the input as a
 * truly free-form custom label.
 */
export const findExtendedOption = (
  keyOrLabel: string | null | undefined,
): ExtendedRelationshipOption | null => {
  if (!keyOrLabel) return null;
  const trimmed = keyOrLabel.trim();
  if (!trimmed) return null;
  const exact = EXTENDED_RELATIONSHIP_OPTIONS.find((o) => o.value === trimmed);
  if (exact) return exact;
  const lower = trimmed.toLowerCase();
  const byOverride = EXTENDED_RELATIONSHIP_OPTIONS.find(
    (o) => (o.displayOverride ?? '').trim().toLowerCase() === lower,
  );
  if (byOverride) return byOverride;
  // Also match against the visible label so a quick-add button labeled
  // "Niece / Nephew" resolves to the virtual entry without needing a
  // separate displayOverride lookup.
  const byLabel = EXTENDED_RELATIONSHIP_OPTIONS.find(
    (o) => o.label.trim().toLowerCase() === lower,
  );
  return byLabel ?? null;
};


export type BirthdayVisibility = 'public' | 'private' | 'year_only';

/**
 * Profile state controls who is allowed to edit a member's personal info.
 *  - 'placeholder': created by another family member; the original creator
 *    can still edit until a real user claims the profile.
 *  - 'claimed': a verified, signed-in user has taken ownership. Only that
 *    user can edit personal info (name / photo / bio / memories). Core
 *    relationships (parents / children / spouse) remain editable by the
 *    family-graph admin/owner only.
 */
export type ProfileState = 'placeholder' | 'claimed';

export type TreeWorkflowKind =
  | 'structural'
  | 'spouse-partner'
  | 'former-spouse-partner'
  | 'co-parent'
  | 'step-family'
  | 'in-law'
  | 'guardian'
  | 'foster-family'
  | 'god-family'
  | 'chosen-family'
  | 'affiliated-family'
  | 'display-only';

export type TreePartnershipStatus =
  | 'current'
  | 'former'
  | 'coparent-only'
  | 'not-applicable';

export type TreeNamePreference =
  | 'real-name'
  | 'nickname';

export interface TreeRelationshipWorkflow {
  kind: TreeWorkflowKind;
  connectorMemberId?: string | null;
  secondaryConnectorMemberId?: string | null;
  partnershipStatus?: TreePartnershipStatus;
  createsAncestry: boolean;
  inheritsGeneration: boolean;
  inheritsBranch: boolean;
  preferredFamilyTitle?: string | null;
}

export interface GraphMember {
  id: string;
  ownerUserId: string;
  name: string;
  /** Nickname / "what we call them" — shown in parens after the name. */
  nickname?: string | null;
  /**
   * Controls only the compact Tree-card name.
   * The required profile identity remains `name`.
   */
  treeNamePreference?: TreeNamePreference;
  relationship: Relationship;
  side: Side;
  photoUrl?: string | null;
  birthYear?: number | null;
  birthDate?: string | null;
  birthMonth?: number | null;
  birthDay?: number | null;
  birthdayVisibility?: BirthdayVisibility;
  birthdayReminders?: boolean;
  livingStatus?: LivingStatus;
  notes?: string | null;
  city?: string | null;
  description?: string | null;
  approvalStatus: ApprovalStatus;
  confirmationsCount: number;
  createdBy?: string | null;
  createdAt: string;
  /** IDs of family_graph_members rows that are this person's parents.
   *  Used to auto-derive grandparents/great-grandparents/aunts/uncles/etc. */
  parentIds?: string[];
  /** Optional reciprocal spouse link (other member's id). */
  spouseId?: string | null;
  /** Identity-claim system (see ProfileState). */
  profileState: ProfileState;
  /** auth.users.id of the real person who owns this profile (after claim). */
  claimedByUserId?: string | null;
  claimedAt?: string | null;

/** Hidden internal email used ONLY for private messaging. */
authEmail?: string | null;
  authEmail?: string | null;
  /**
   * Optional admin / profile-owner display override. When set, this takes
   * precedence over the auto-derived kinship label in `viewerRelationshipLabel`.
   * Used for special family situations (e.g. "Godmother", "Adopted aunt",
   * "Step-cousin once removed") that the generational engine can't infer.
   * Never overrides the structural `relationship` column used for tree
   * placement — purely a display tweak.
   */
  relationshipOverride?: string | null;
  /**
   * Branch-scope tag (Nov 2026 — global branch isolation).
   *
   * When set, this row is part of a SPECIFIC family-member's branch
   * (e.g. a claimed member added in-laws or other-parent-side relatives).
   * Centered-tree views hide branch-scoped rows whose `branchOwnerMemberId`
   * is NOT the centered viewer themselves AND NOT an ancestor of the
   * centered viewer (i.e. the centered viewer is not a descendant of the
   * branch owner). Null = unrestricted (visible to anyone in the loaded
   * graph). Persisted in the `metadata` jsonb column under the
   * `branch_owner_member_id` key — no schema change required. Pure
   * presentation hint; never affects parent_ids / spouse_id / kinship.
   */
  branchOwnerMemberId?: string | null;
  /**
   * Governed relationship workflow descriptor.
   *
   * Persisted inside metadata.relationship_workflow. It supplements the
   * backward-compatible relationship enum without inventing parent_ids or
   * spouse_id links.
   */
  relationshipWorkflow?: TreeRelationshipWorkflow | null;
}




// Privacy levels for memories / photos / videos / life events.
export type EntryPrivacy = 'family' | 'private' | 'shared';
// What kind of timeline entry a memory row represents.
export type EntryKind = 'memory' | 'story' | 'life_event';
// How precise the event_date is — drives display.
export type DatePrecision = 'exact' | 'month' | 'year' | 'approximate' | 'unknown';

export interface GraphMemory {
  id: string;
  memberId: string;
  text: string;
  // Rich metadata
  title?: string | null;
  eventDate?: string | null;     // ISO date or null when unknown
  datePrecision?: DatePrecision;
  location?: string | null;
  taggedMemberIds?: string[];
  privacy?: EntryPrivacy;
  lifeStage?: string | null;     // Manual override (rarely used)
  entryKind?: EntryKind;
  createdBy?: string | null;
  createdAt: string;
}

export interface GraphPhoto {
  id: string;
  memberId: string;
  url: string;
  storagePath?: string | null;
  caption?: string | null;
  mediaType: 'image' | 'video';
  // Rich metadata so photos / videos slot into the timeline alongside memories
  title?: string | null;
  eventDate?: string | null;
  datePrecision?: DatePrecision;
  location?: string | null;
  taggedMemberIds?: string[];
  /**
   * "Who is this memory about?" — the member ids that the upload is
   * assigned to. Persisted in the SAME `tagged_member_ids` column (no
   * schema change) and exposed as a parallel alias for callers that
   * prefer the subject-centric vocabulary. When set, this memory shows
   * up on each subject's Journey timeline (in addition to the anchor
   * memberId of the upload). An empty / missing list means the memory
   * is a general "Family memory" and only appears on the anchor profile
   * + the family-wide feed.
   */
  subjectMemberIds?: string[];
  privacy?: EntryPrivacy;
  /** When privacy === 'shared': claimed-member ids of the people allowed to see this. */
  sharedWithMemberIds?: string[];
  createdBy?: string | null;
  createdAt: string;
}

export interface GraphComment {
  id: string;
  memberId: string;
  entryType: 'memory' | 'photo';
  entryId: string;
  text: string;
  authorUserId?: string | null;
  authorName?: string | null;
  createdAt: string;
}

export interface MemoryInput {
  text: string;
  title?: string | null;
  eventDate?: string | null;
  datePrecision?: DatePrecision;
  location?: string | null;
  taggedMemberIds?: string[];
  privacy?: EntryPrivacy;
  entryKind?: EntryKind;
  lifeStage?: string | null;
}

export interface PhotoMeta {
  title?: string | null;
  eventDate?: string | null;
  datePrecision?: DatePrecision;
  location?: string | null;
  taggedMemberIds?: string[];
  /**
   * NEW — "Who is this memory about?" subject assignment.
   *
   * Additive parallel field that maps to the SAME `tagged_member_ids`
   * column as `taggedMemberIds`. Provided so callers (esp. the upload
   * modal) can express subject assignment with semantic clarity:
   *
   *   meta = { subjectMemberIds: [aliceId, bobId] }
   *
   * — and the memory will appear on Alice's AND Bob's Journey timelines
   * without duplicating the underlying file. When BOTH `taggedMemberIds`
   * and `subjectMemberIds` are passed, the union is written. When NEITHER
   * is passed, the memory stays as a general family memory tied only to
   * the upload's anchor `memberId`.
   */
  subjectMemberIds?: string[];
  privacy?: EntryPrivacy;
  /** Required when privacy === 'shared'. Member IDs (claimed profiles) that can view. */
  sharedWithMemberIds?: string[];
}


export type AddMemberInput = Omit<
  GraphMember,
  'id' | 'ownerUserId' | 'createdAt' | 'createdBy' | 'approvalStatus' | 'confirmationsCount'
> & { approvalStatus?: ApprovalStatus };

/** A potential identity match returned by `searchClaimCandidates`. */
export interface ClaimCandidate {
  member: GraphMember;
  /** Display name of the inviter / family graph this profile lives on. */
  ownerLabel?: string | null;
  /** A human-readable explanation of why this is a likely match. */
  reasonLabel: string;
  /** Heuristic confidence 0..1 — higher = more likely the same person. */
  score: number;
}

export interface MergeRequest {
  id: string;
  ownerUserId: string;
  sourceMemberId: string;
  targetMemberId: string;
  requestedBy: string;
  reason?: string | null;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
  resolvedAt?: string | null;
  resolvedBy?: string | null;
}

interface FamilyGraphContextType {
  members: GraphMember[];
  memories: GraphMemory[];
  photos: GraphPhoto[];
  comments: GraphComment[];
  loading: boolean;
  /**
   * The auth.users.id of the family-graph owner whose rows are currently
   * loaded. In the common single-user case this equals the logged-in
   * user.id. When the logged-in user is an INVITED / claimed member who
   * lives inside another family member's existing graph, this is the
   * graph owner's id (so all rows for that shared family tree load).
   * Mutations write to this owner's graph.
   */
  effectiveOwnerUserId: string | null;
  /**
   * The id of the `family_graph_members` row that represents the
   * currently-logged-in user inside the loaded graph (i.e. the row whose
   * `claimed_by_user_id` matches the auth user). Drives "viewer-centered"
   * tree rendering: this row becomes the YOU anchor and every other
   * member's relationship label is computed relative to it. Null when
   * the logged-in user has not claimed any profile yet — in that case
   * the viewer falls back to the implicit graph-owner self anchor.
   */
  viewerMemberId: string | null;
  /**
   * Read-only list of UNCLAIMED rows in the loaded graph that look like
   * they could be the currently-logged-in user (name match against the
   * auth profile name). Populated by `refresh()` ONLY when the standard
   * claim resolution + viewer-mapping repair did NOT auto-bind a row
   * AND there are 1..N likely matches by name. Used by the
   * IdentityBindingModal to ask "Which person in this family tree are
   * you?". Cleared once `bindViewerToMember()` (or any other claim flow)
   * succeeds. Pure presentation hint — never mutates any row.
   */
  viewerCandidates: GraphMember[];
  /**
   * Last error encountered during `refresh()` member hydration. Null when
   * the most recent refresh completed without any query error. Surfaced
   * read-only via the System Status identity summary so a stuck refresh
   * (RLS rejection, PostgREST `.or()` syntax issue, network timeout) is
   * visible instead of silent. Never mutated by any other code path.
   */
  lastRefreshError: string | null;
  /**
   * Last resolved `targetOwner` (effective graph owner) from `refresh()`.
   * Useful in the identity summary to confirm we're loading the expected
   * graph. Equal to `effectiveOwnerUserId` once refresh completes, but
   * exposed separately so the summary can distinguish "we resolved an
   * owner but the load failed" from "owner resolution itself failed".
   */
  lastTargetOwner: string | null;
  /**
   * Number of rows the most recent refresh hydrated into `members[]`.
   * Mirrors `members.length` after a successful refresh; remains the
   * count from the LAST refresh while a new one is in flight. Pure
   * diagnostic — never mutated by mutators like addMember.
   */
  lastFetchedCount: number;
  /**
   * Explicit "this row is me" binding. Wraps `claimProfile()` for the
   * IdentityBindingModal: the user picks one of the `viewerCandidates`
   * (or any other row), we claim it, the next refresh re-anchors the
   * tree on the now-bound row. Resolves once the claim has landed in
   * the local cache so the caller can immediately read `viewerMemberId`.
   * Throws the same errors as `claimProfile()` (already-claimed, etc.).
   */
  bindViewerToMember: (memberId: string) => Promise<void>;
  addMember: (input: AddMemberInput) => Promise<GraphMember | null>;
  updateMember: (id: string, updates: Partial<GraphMember>) => Promise<void>;
  approveMember: (id: string) => Promise<void>;
  rejectMember: (id: string) => Promise<void>;
  deleteMember: (
    request: GatewayDeleteMemberRequest,
  ) => Promise<void>;
  /**
   * Non-interactive cleanup for a member created during the current
   * add-relative transaction and immediately proven duplicate.
   * Routes through gatewayDeleteMember and never opens a confirmation UI.
   */
  rollbackCreatedMember: (
    member: GraphMember,
    reason?: string | null,
  ) => Promise<void>;
  restoreMember: (id: string) => Promise<void>;

  // New rich addMemory: pass either a string (legacy) or full MemoryInput.
  addMemory: (memberId: string, input: string | MemoryInput) => Promise<GraphMemory | null>;
  updateMemory: (id: string, updates: Partial<MemoryInput> & { text?: string }) => Promise<void>;
  deleteMemory: (id: string) => Promise<void>;
  confirmMember: (id: string) => Promise<void>;
  addPhoto: (
    memberId: string,
    url: string,
    storagePath?: string | null,
    caption?: string | null,
    mediaType?: 'image' | 'video',
    meta?: PhotoMeta
  ) => Promise<GraphPhoto | null>;
  updatePhoto: (id: string, updates: PhotoMeta & { caption?: string | null }) => Promise<void>;
  deletePhoto: (id: string) => Promise<void>;
  // Comments
  addComment: (
    memberId: string,
    entryType: 'memory' | 'photo',
    entryId: string,
    text: string,
    authorName?: string | null
  ) => Promise<GraphComment | null>;
  deleteComment: (id: string) => Promise<void>;
  findPossibleDuplicates: (
    name: string,
    relationship: Relationship,
    side: Side,
    birthYear?: number | null
  ) => GraphMember[];
  /**
   * Set / replace the parent_ids list for a member. Inverse-link is handled
   * implicitly: parent's relationship to "you" stays as stored, but their
   * grandparent/great-grandparent chain is recomputed for everyone.
   */
  connectExistingAncestryAtomic: (
    request:
      GatewayConnectExistingAncestryRequest,
  ) => Promise<
    GatewayConnectExistingAncestryResult
  >;

  linkParents: (memberId: string, parentIds: string[]) => Promise<void>;
  /** Two-way spouse link. Sets spouse_id on both members. */
  linkSpouses: (aId: string, bId: string | null) => Promise<void>;
  /**
   * Force-link two existing members as spouses via a direct DB write +
   * verification round-trip. Bypasses any closure-stale state. Used by
   * the manual "Repair spouse link" recovery flow when an earlier
   * spouse-add silently dropped the bidirectional spouse_id (e.g.
   * Terrance Lewis Sr. ↔ Chekesha Graham-Lewis). Pure additive — never
   * mutates parent_ids and never deletes the person row.
   */
  repairSpouseEdge: (
    memberAId: string,
    memberBId: string,
  ) => Promise<{ success: boolean; verified: boolean; error?: string }>;


  // ---------- Identity / claim system ----------
  /**
   * Search every family graph for unclaimed placeholder profiles that look
   * like the given identity. Used right after a real user signs up to ask
   * "We found your profile. Is this you?".
   */
  searchClaimCandidates: (input: {
    name: string;
    birthYear?: number | null;
    birthDate?: string | null;
    excludeOwnerUserId?: string | null;
  }) => Promise<ClaimCandidate[]>;
  /**
   * Take ownership of a placeholder profile. Idempotent — re-claiming your
   * own profile is a no-op. Fails if another user already claimed it.
   */
  claimProfile: (memberId: string) => Promise<void>;
  /** Release ownership (only the current claim-owner can do this). */
  unclaimProfile: (memberId: string) => Promise<void>;
  /**
   * File a duplicate-merge request (e.g. "this profile and that profile are
   * the same person"). The owner of the family graph that holds the target
   * approves or rejects.
   */
  requestMerge: (
    sourceMemberId: string,
    targetMemberId: string,
    reason?: string
  ) => Promise<MergeRequest | null>;

  /**
   * True while one or more relationship writes (addMember / updateMember /
   * linkParents / linkSpouses) are in flight, OR while a `runBulkAdd`
   * batch is open. Consumers (e.g. the tree renderer) read this to
   * temporarily LOCK the on-screen layout — no auto-fit zoom, no
   * recenter — so a rapid sequence of additions doesn't make the tree
   * jump / collapse / inflate between every micro-step. Flips back to
   * false once the queue is empty AND any open bulk batch has closed.
   *
   * Pure presentation hint — never blocks any write, never affects
   * data correctness, never modifies parent_ids / spouse_id / billing /
   * auth / invites / uploads / Resend / onboarding / schema.
   */
  isBulkWriting: boolean;

  /**
   * Run `fn` as a single LOGICAL bulk-add batch. While `fn` is executing
   * (and any awaited writes inside it complete), `isBulkWriting` stays
   * true so the tree layout stays locked. After `fn` resolves, a single
   * debounced `refresh()` is scheduled — replacing N per-operation
   * refresh calls with ONE coalesced refresh once the user is done.
   *
   * The function is re-entrant: nesting calls just increments the bulk
   * depth. Errors thrown by `fn` are re-thrown to the caller, with the
   * bulk depth still correctly decremented in `finally`.
   *
   * Designed for AddGraphMemberModal's save flow, where each save runs
   * an `addMember` followed by 0..N `linkParents` / `linkSpouses`
   * follow-ups — wrapping the whole save in `runBulkAdd` collapses the
   * entire sequence into ONE layout-stable batch.
   */
  runBulkAdd: <T>(fn: () => Promise<T>) => Promise<T>;

  /**
   * Request a refresh of the loaded family graph, debounced. Multiple
   * calls within ~180ms collapse into a single `refresh()` invocation.
   * Use this in place of `await refresh()` from save flows that may run
   * back-to-back so we don't stampede the database with N refresh
   * round-trips when a user adds several relatives quickly.
   */
  scheduleRefresh: () => void;

  refresh: () => Promise<void>;
}




const FamilyGraphContext = createContext<FamilyGraphContextType | undefined>(undefined);

const rowToMember:
  (r: any) => GraphMember =
    mapTreeRuntimeGraphMemberRow;




const rowToMemory = (r: any): GraphMemory => ({
  id: r.id,
  memberId: r.member_id,
  text: r.text,
  title: r.title ?? null,
  eventDate: r.event_date ?? null,
  datePrecision: (r.date_precision as DatePrecision) ?? 'exact',
  location: r.location ?? null,
  taggedMemberIds: r.tagged_member_ids ?? [],
  privacy: (r.privacy as EntryPrivacy) ?? 'family',
  lifeStage: r.life_stage ?? null,
  entryKind: (r.entry_kind as EntryKind) ?? 'memory',
  createdBy: r.created_by ?? null,
  createdAt: r.created_at,
});

const rowToPhoto = (r: any): GraphPhoto => {
  // Persisted column = `tagged_member_ids` (no schema change). We expose it
  // under both `taggedMemberIds` (legacy) AND `subjectMemberIds` (new
  // subject-assignment vocabulary) so consumers can read either field.
  // They are always identical.
  const ids: string[] = Array.isArray(r.tagged_member_ids) ? r.tagged_member_ids : [];
  return {
    id: r.id,
    memberId: r.member_id,
    url: r.url,
    storagePath: r.storage_path ?? null,
    caption: r.caption ?? null,
    mediaType: r.media_type === 'video' ? 'video' : 'image',
    title: r.title ?? null,
    eventDate: r.event_date ?? null,
    datePrecision: (r.date_precision as DatePrecision) ?? 'exact',
    location: r.location ?? null,
    taggedMemberIds: ids,
    subjectMemberIds: ids,
    privacy: (r.privacy as EntryPrivacy) ?? 'family',
    sharedWithMemberIds: Array.isArray(r.shared_with_member_ids) ? r.shared_with_member_ids : [],
    createdBy: r.created_by ?? null,
    createdAt: r.created_at,
  };
};



const rowToComment = (r: any): GraphComment => ({
  id: r.id,
  memberId: r.member_id,
  entryType: (r.entry_type as 'memory' | 'photo') ?? 'memory',
  entryId: r.entry_id,
  text: r.text,
  authorUserId: r.author_user_id ?? null,
  authorName: r.author_name ?? null,
  createdAt: r.created_at,
});

const norm = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

/**
 * Loose name overlap heuristic — kept for the claim-candidate flow where
 * we already gate on birth-year delta. NOT used for in-tree duplicate
 * detection any more (that path uses `isStrongNameMatch` below, which
 * requires first AND last token agreement so single shared initials /
 * shared last names alone never trigger a false "possible duplicate").
 */
const nameSimilar = (a: string, b: string) => {
  const an = norm(a);
  const bn = norm(b);
  if (!an || !bn) return false;
  if (an === bn) return true;
  if (an.includes(bn) || bn.includes(an)) return true;
  const aTokens = an.split(' ');
  const bTokens = bn.split(' ');
  const overlap = aTokens.filter((t) => bTokens.includes(t)).length;
  return overlap >= 2;
};

/**
 * Tokenize a name for STRICT duplicate detection. Drops single-character
 * tokens (middle initials like "J." in "Earnest J. Walker") so a shared
 * initial never counts toward a "match". Lowercased + punctuation stripped.
 */
const strictTokens = (s: string): string[] =>
  norm(s)
    .split(' ')
    .filter((t) => t.length >= 2);

/**
 * STRICT same-person matcher used by `findPossibleDuplicates`.
 *
 * Returns:
 *   { match: false }                       → not a likely duplicate.
 *   { match: true, exact: true }           → identical full normalized name
 *                                            (callers may treat as blocking
 *                                            when DOB also matches).
 *   { match: true, exact: false }          → first-name AND last-name agree
 *                                            (with allowance for short
 *                                            prefix variants like Bob/Bobby
 *                                            when both have length >= 3).
 *
 * Critically — sharing only a middle initial OR only a last name OR only
 * a first name does NOT match. Example:
 *   "Earnest J. Walker" vs "Princess J. Walker" → first tokens differ
 *   ("earnest" ≠ "princess"), so NO match — even though both share the
 *   "J." initial and the "Walker" surname. This is the exact false-
 *   positive the user reported.
 */
const isStrongNameMatch = (
  a: string,
  b: string,
): { match: boolean; exact: boolean } => {
  const an = norm(a);
  const bn = norm(b);
  if (!an || !bn) return { match: false, exact: false };
  if (an === bn) return { match: true, exact: true };
  const aTokens = strictTokens(a);
  const bTokens = strictTokens(b);
  if (aTokens.length < 2 || bTokens.length < 2) return { match: false, exact: false };
  const aFirst = aTokens[0];
  const aLast = aTokens[aTokens.length - 1];
  const bFirst = bTokens[0];
  const bLast = bTokens[bTokens.length - 1];
  const firstMatch =
    aFirst === bFirst ||
    (aFirst.length >= 3 &&
      bFirst.length >= 3 &&
      (aFirst.startsWith(bFirst) || bFirst.startsWith(aFirst)));
  const lastMatch = aLast === bLast;
  return { match: firstMatch && lastMatch, exact: false };
};



export const FamilyGraphProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();

  const externalEvidenceRegistryRef =
    useRef(
      getCanonicalTreeTabScopedSanitizedEvidenceRegistry(),
    );

  const authenticatedOwnerPresentRef =
    useRef<boolean>(
      Boolean(user?.id),
    );

  authenticatedOwnerPresentRef.current =
    Boolean(user?.id);

  const [members, setMembers] = useState<GraphMember[]>([]);
  const [memories, setMemories] = useState<GraphMemory[]>([]);
  const [photos, setPhotos] = useState<GraphPhoto[]>([]);
  const [comments, setComments] = useState<GraphComment[]>([]);
  const [loading, setLoading] = useState(false);

  // Monotonic authority for overlapping graph refreshes.
  // Only the newest refresh generation may commit context state.
  const refreshGenerationRef = useRef<number>(0);

  // Private context-owned external-evidence lifecycle.
  // Not exposed through FamilyGraphContextType and not transported to the gateway.
  const externalEvidenceContextRef =
    useRef<CanonicalTreeExternalEvidenceContextState>(
      createCanonicalTreeExternalEvidenceContextState({
        gatewayTransportEnabled: false,
      }),
    );

  const commitExternalEvidenceContext = useCallback(
    (
      next:
        CanonicalTreeExternalEvidenceContextState,
    ): void => {
      externalEvidenceContextRef.current =
        next;

      let summary:
        ReturnType<
          typeof summarizeCanonicalTreeExternalEvidenceContext
        >;

      try {
        summary =
          summarizeCanonicalTreeExternalEvidenceContext(
            next,
          );
      } catch {
        // Sanitized diagnostic projection failure is non-fatal.
        return;
      }

      try {
        externalEvidenceRegistryRef.current.publishActive({
          authenticatedOwnerPresent:
            authenticatedOwnerPresentRef.current,

          candidate:
            summary,
        });
      } catch {
        // Registry publication is diagnostic and cannot interrupt graph runtime.
      }
    },
    [],
  );

  const invalidateExternalEvidence = useCallback(
    (
      refreshReason:
        | 'MEMBERS-CHANGED'
        | 'SPOUSE-WRITE',
    ): void => {
      commitExternalEvidenceContext(
        invalidateCanonicalTreeExternalEvidenceContextState({
          current:
            externalEvidenceContextRef.current,
          refreshReason,
          gatewayTransportEnabled:
            false,
        }),
      );
    },
    [
      commitExternalEvidenceContext,
    ],
  );

  useEffect(() => {
    commitExternalEvidenceContext(
      externalEvidenceContextRef.current,
    );

    return () => {
      try {
        externalEvidenceRegistryRef.current.markPublisherUnmounted({
          authenticatedOwnerPresent:
            authenticatedOwnerPresentRef.current,
        });
      } catch {
        // Registry lifecycle disposal is diagnostic and non-fatal.
      }
    };
  }, [
    commitExternalEvidenceContext,
  ]);

  /**
   * The auth user whose family graph rows are loaded. Defaults to the
   * logged-in user (single-user / "owns my own tree" path). When the
   * logged-in user is an INVITED relative who has claimed a profile that
   * lives inside another family member's existing graph, we switch this
   * to that owner so we load the SHARED tree instead of an empty one.
   */
  const [effectiveOwnerUserId, setEffectiveOwnerUserId] = useState<string | null>(null);
  /**
   * Id of the family_graph_members row that represents the currently-
   * logged-in user inside the loaded graph. Drives viewer-centered tree
   * rendering. Null until we discover a row claimed by `user.id`.
   */
  const [viewerMemberId, setViewerMemberId] = useState<string | null>(null);

  /**
   * Read-only list of UNCLAIMED rows in the loaded graph that look like
   * they could be the currently-logged-in user (name match against the
   * auth profile name) — populated when the auto-bind ladder cannot
   * pick a single high-confidence candidate. The IdentityBindingModal
   * reads this to ask "Which person in this family tree are you?".
   * Cleared when `bindViewerToMember()` (or any other claim flow)
   * succeeds. See `bindViewerToMember` below for the explicit
   * binding API. Pure presentation — never mutates any row.
   */
  const [viewerCandidates, setViewerCandidates] = useState<GraphMember[]>([]);

  // ─── Refresh diagnostic state (May 2026 — empty members[] visibility) ──
  // Tiny read-only surface so a stuck refresh is never silent. These three
  // values are written exclusively from inside refresh()'s try/catch and
  // surfaced via the context value so the System Status panel can render
  // them in the identity summary. They never affect any write path.
  const [lastRefreshError, setLastRefreshError] = useState<string | null>(null);
  const [lastTargetOwner, setLastTargetOwner] = useState<string | null>(null);
  const [lastFetchedCount, setLastFetchedCount] = useState<number>(0);




  // ─── BULK-ADD STABILITY INFRASTRUCTURE ─────────────────────────────────
  //
  // Real users add multiple relatives in quick succession during onboarding
  // (mom → dad → maternal grandma → grandpa → siblings → aunts → cousins).
  // Without protection each save triggers its own refresh + tree auto-fit,
  // which makes the layout jump / collapse / inflate between every step
  // and creates the "I have to refresh after every person" feeling.
  //
  // The infrastructure below provides:
  //   1. A bulk-write depth counter so callers can wrap a save sequence
  //      in `runBulkAdd(fn)` and have `isBulkWriting` stay true across
  //      every internal step (addMember + linkParents + linkSpouses).
  //   2. `scheduleRefresh()` — a debounced refresh that collapses N
  //      back-to-back refresh requests into ONE coalesced re-fetch.
  //      ~180ms is long enough to batch the typical save-then-refresh
  //      pattern while staying snappy enough that the freshly-saved row
  //      reaches the tree without perceptible delay.
  //   3. `isBulkWriting` flips to false only after both the depth is
  //      zero AND the debounce timer has fired the trailing refresh.
  //
  // PURE infrastructure. No data mutation. No schema change. parent_ids /
  // spouse_id / billing / auth / invites / uploads / Resend / onboarding /
  // Feature Guide / Health Panel are never touched.
  const bulkDepthRef = useRef<number>(0);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshPendingRef = useRef<boolean>(false);
  const [isBulkWriting, setIsBulkWriting] = useState<boolean>(false);

  // ─── Sequential write queue (mutex) ────────────────────────────────────
  // Real users — especially during onboarding — fire several add-relative
  // calls back-to-back (mom → dad → grandparent → sibling → aunt → cousin).
  // Without serialization, two save handlers can interleave their
  // addMember / linkParents / linkSpouses round-trips and produce subtle
  // races: e.g. addMember A's optimistic setMembers update has not yet
  // committed when linkParents B reads `members` to compute the cycle
  // guard, so B's parent reference looks unknown and the engine has to
  // trust-the-id (which it now does — see linkParents notes). The queue
  // below removes that race entirely by making EVERY relationship-affecting
  // write run end-to-end before the next one starts.
  //
  // Implementation: a single in-flight Promise chain. `enqueueWrite(fn)`
  // appends `fn` to the chain and returns a Promise that resolves with
  // `fn`'s result. Errors thrown inside `fn` propagate to that caller
  // ONLY — the chain itself swallows them so a single failed save can
  // never poison the queue for subsequent saves.
  //
  // Pure infrastructure. Does not touch parent_ids semantics, billing,
  // FamousPay, auth, invites, uploads, Resend, onboarding, Feature Guide,
  // ─── Sequential write helper (NON-BLOCKING — stability hotfix) ────────
  // EARLIER REGRESSION: a chained Promise queue here could deadlock the
  // entire add-relative flow — if any single queued write threw an
  // exception in an unexpected place (or the trailing refresh hung), the
  // chain head was left in a state where every subsequent enqueueWrite
  // call awaited a promise that never resolved. The user-visible symptom
  // was "I added one cousin, now the modal save spins forever and I can't
  // add anyone else without a full page refresh".
  //
  // The hotfix: run `fn` directly and return its promise. The original
  // race the queue tried to solve (linkParents reading a stale members
  // closure right after addMember) is already fully covered by:
  //   1. `addMember` returning the database-confirmed row, which the
  //      caller (AddGraphMemberModal) uses as `created.id` for any
  //      follow-up linkParents / linkSpouses call.
  //   2. `linkParents` no longer filtering candidate ids against the
  //      local members closure — it trusts the caller's id and lets the
  //      DB write be the source of truth.
  // So serialization is no longer required for correctness, and removing
  // it eliminates the only known stuck-state in the save path. Pure
  // safety unwind — no schema change, no parent_ids semantic change, no
  // billing / FamousPay / auth / invites / uploads / Resend / onboarding /
  // Feature Guide / Health Panel / database touched.
  const enqueueWrite = useCallback(<T,>(fn: () => Promise<T>): Promise<T> => {
    return fn();
  }, []);





  /**
   * Resolve which family-graph "owner_user_id" to load for the given auth
   * user. Order:
   *
   *   1. If the user has at least one row anywhere with
   *      `claimed_by_user_id = user.id`, prefer the OWNER of the graph
   *      that contains that claim. This is the "I was invited, I claimed
   *      my placeholder, now show me the SHARED family tree" path.
   *      If multiple owners host a claimed row for this user, prefer the
   *      one with the most members so we always land in the largest /
   *      most-connected tree.
   *   2. NEW PROTECTION — if the user has NO claims yet, but their
   *      `profiles.invited_by` is set AND the inviter has a non-trivial
   *      family tree (>= 2 members), route them into the INVITER's tree
   *      instead of their own empty/blank tree. This is the safety net
   *      that prevents "I was invited, I logged in, I see a blank tree"
   *      when the auto-claim step at signup didn't match an existing
   *      placeholder row. We do NOT auto-create a claim — that still
   *      requires explicit identity confirmation — but the invited user
   *      now lands in the SHARED tree they were invited to from the
   *      first login, exactly as the inviter sees it.
   *   3. Otherwise default to the user's own owner_user_id (the legacy
   *      single-user "I built my own tree from scratch" path).
   *
   * Also returns the viewer member id (the row claimed by the user inside
   * the chosen graph), if any.
   */
  const resolveEffectiveOwner = useCallback(
    async (
      userId: string
    ): Promise<{ ownerUserId: string; viewerId: string | null }> => {
      try {
        const { data: claimedRows } = await supabase
          .from('family_graph_members')
          .select('id, owner_user_id')
          .eq('claimed_by_user_id', userId)
          .is('deleted_at', null);
        const claims = claimedRows ?? [];
        if (claims.length === 0) {
          // ── Protection step 2: no explicit claim row for this user.
          // Look up profiles.invited_by — if the invited-by user has a
          // populated tree, route the invitee into THAT tree so they see
          // the shared family graph their inviter is building, not an
          // empty self-tree. Best-effort: any failure silently falls
          // back to the user's own owner_user_id.
          try {
            const { data: profile } = await supabase
              .from('profiles')
              .select('invited_by')
              .eq('id', userId)
              .maybeSingle();
            const inviterId = (profile as any)?.invited_by as string | null | undefined;
            if (inviterId && inviterId !== userId) {
              const { count: inviterTreeCount } = await supabase
                .from('family_graph_members')
                .select('*', { count: 'exact', head: true })
                .eq('owner_user_id', inviterId)
                .is('deleted_at', null);
              if ((inviterTreeCount ?? 0) >= 2) {
                // Land the invited user inside the inviter's shared
                // tree. viewerId stays null until they explicitly claim
                // a placeholder profile (the existing claim flow handles
                // the binding) — but the tree itself is now visible.
                return { ownerUserId: inviterId, viewerId: null };
              }
            }
          } catch {
            /* non-fatal — fall through to default */
          }
          return { ownerUserId: userId, viewerId: null };
        }
        // Group by owner_user_id; prefer the owner with the most members.
        const ownerCandidates = Array.from(
          new Set(claims.map((r: any) => r.owner_user_id as string))
        );
        if (ownerCandidates.length === 1) {
          const owner = ownerCandidates[0];
          const claim = claims.find((c: any) => c.owner_user_id === owner)!;
          return { ownerUserId: owner, viewerId: claim.id };
        }
        // Multiple candidate graphs — pick the biggest. Best-effort count
        // query; on failure fall back to the first claim.
        let bestOwner = ownerCandidates[0];
        let bestCount = -1;
        for (const owner of ownerCandidates) {
          const { count } = await supabase
            .from('family_graph_members')
            .select('*', { count: 'exact', head: true })
            .eq('owner_user_id', owner)
            .is('deleted_at', null);
          if ((count ?? 0) > bestCount) {
            bestCount = count ?? 0;
            bestOwner = owner;
          }
        }
        const claim = claims.find((c: any) => c.owner_user_id === bestOwner)!;
        return { ownerUserId: bestOwner, viewerId: claim.id };
      } catch (err) {
        console.warn('[FamilyGraph] resolveEffectiveOwner failed', err);
        return { ownerUserId: userId, viewerId: null };
      }
    },
    []
  );


  const refresh = useCallback(async () => {
    const refreshGeneration =
      ++refreshGenerationRef.current;

    const isCurrentRefresh = (): boolean =>
      refreshGenerationRef.current ===
      refreshGeneration;

    if (!user?.id) {
      setMembers([]);
      setMemories([]);
      setPhotos([]);
      setComments([]);
      setEffectiveOwnerUserId(null);
      setViewerMemberId(null);
      setLastRefreshError(null);
      setLastTargetOwner(null);
      setLastFetchedCount(0);
      setLoading(false);

      commitExternalEvidenceContext(
        createCanonicalTreeExternalEvidenceContextState({
          gatewayTransportEnabled: false,
        }),
      );

      return;
    }

    setLoading(true);
    // Track the most-recent refresh error so the System Status identity
    // summary can surface a stuck refresh instead of silently leaving
    // bindingState='loading'. Reset to null at the start of every attempt.
    let refreshError: string | null = null;
    let targetOwner: string = user.id;
    try {
      // Decide which graph to load (own vs. shared / invited graph).
      const ownerResolution = await resolveEffectiveOwner(user.id);

      if (!isCurrentRefresh()) {
        return;
      }

      targetOwner = ownerResolution.ownerUserId;
      const viewerId = ownerResolution.viewerId;

      const previousEvidenceContext =
        externalEvidenceContextRef.current;

      const evidenceOwnerChanged =
        Boolean(
          previousEvidenceContext.ownerUserId &&
          previousEvidenceContext.ownerUserId !==
            targetOwner,
        );

      if (evidenceOwnerChanged) {
        commitExternalEvidenceContext(
          createCanonicalTreeExternalEvidenceContextState({
            gatewayTransportEnabled:
              false,
          }),
        );
      }

      setEffectiveOwnerUserId(targetOwner);
      setLastTargetOwner(targetOwner);

      // ─── EFFECTIVE GRAPH HYDRATION BOUNDARY ──────────────────────────
      // Production Tree members are owned exclusively by targetOwner.
      //
      // The user-scoped query remains read-only viewer-resolution evidence.
      // Its rows must never enter canonical projection, topology, geometry,
      // discrepancy evidence, or the rendered members collection unless
      // they independently belong to the effective graph.
      const authUserId = user.id;
      const userScopedOrClause = [
        `owner_user_id.eq.${authUserId}`,
        `claimed_by_user_id.eq.${authUserId}`,
      ].join(',');

      const [graphMemRes, userMemRes, memoRes, photoRes, commentRes] =
        await Promise.all([
          supabase
            .from('family_graph_members')
            .select('*')
            .eq('owner_user_id', targetOwner)
            .is('deleted_at', null)
            .order('created_at', { ascending: true }),
          supabase
            .from('family_graph_members')
            .select('id, owner_user_id, claimed_by_user_id, profile_state')
            .or(userScopedOrClause)
            .is('deleted_at', null)
            .order('created_at', { ascending: true }),
          supabase
            .from('family_graph_memories')
            .select('*')
            .eq('owner_user_id', targetOwner)
            .order('created_at', { ascending: false }),
          supabase
            .from('family_graph_photos')
            .select('*')
            .eq('owner_user_id', targetOwner)
            .order('created_at', { ascending: false }),
          supabase
            .from('family_graph_memory_comments')
            .select('*')
            .eq('owner_user_id', targetOwner)
            .order('created_at', { ascending: true }),
        ]);

      if (!isCurrentRefresh()) {
        return;
      }

      const graphErr =
        (graphMemRes as any)?.error ?? null;

      const userErr =
        (userMemRes as any)?.error ?? null;

      if (graphErr) {
        console.warn(
          '[FamilyGraph] effective-graph member query failed',
          graphErr,
        );

        refreshError =
          `effective graph query: ${
            graphErr.message ?? String(graphErr)
          }`;
      }

      if (userErr) {
        console.warn(
          '[FamilyGraph] viewer-evidence query failed',
          userErr,
        );

        refreshError = refreshError
          ? `${refreshError} | viewer evidence: ${
              userErr.message ?? String(userErr)
            }`
          : `viewer evidence: ${
              userErr.message ?? String(userErr)
            }`;
      }

      let graphRows =
        (graphMemRes.data ?? []) as any[];

      // A recovery read may retry only the same effective graph.
      // It must never switch to the authenticated user's own graph.
      if (graphRows.length === 0 && graphErr) {
        try {
          const {
            data: retryRows,
            error: retryErr,
          } = await supabase
            .from('family_graph_members')
            .select('*')
            .eq('owner_user_id', targetOwner)
            .is('deleted_at', null)
            .order('created_at', { ascending: true });

          if (!isCurrentRefresh()) {
            return;
          }

          if (retryErr) {
            console.warn(
              '[FamilyGraph] effective-graph retry failed',
              retryErr,
            );

            refreshError = refreshError
              ? `${refreshError} | effective graph retry: ${
                  retryErr.message ?? String(retryErr)
                }`
              : `effective graph retry: ${
                  retryErr.message ?? String(retryErr)
                }`;
          } else {
            graphRows =
              (retryRows ?? []) as any[];
          }
        } catch (retryEx: any) {
          console.warn(
            '[FamilyGraph] effective-graph retry threw',
            retryEx,
          );

          refreshError = refreshError
            ? `${refreshError} | effective graph retry threw: ${
                retryEx?.message ?? String(retryEx)
              }`
            : `effective graph retry threw: ${
                retryEx?.message ?? String(retryEx)
              }`;
        }
      }

      const hydrationBoundary =
        enforceTreeRuntimeEffectiveGraphHydrationBoundary({
          rows:
            graphRows,
          targetOwnerUserId:
            targetOwner,
        });

      if (
        hydrationBoundary.rejectedCrossGraphRowCount > 0 ||
        hydrationBoundary.rejectedInvalidRowCount > 0
      ) {
        const boundaryMessage =
          `effective graph boundary rejected ` +
          `${hydrationBoundary.rejectedCrossGraphRowCount} cross-graph ` +
          `and ${hydrationBoundary.rejectedInvalidRowCount} invalid row(s)`;

        console.warn(
          '[FamilyGraph] effective graph hydration boundary contained rows',
          {
            targetOwner,
            rejectedCrossGraphRowCount:
              hydrationBoundary.rejectedCrossGraphRowCount,
            rejectedInvalidRowCount:
              hydrationBoundary.rejectedInvalidRowCount,
            duplicateRowCount:
              hydrationBoundary.duplicateRowCount,
          },
        );

        refreshError = refreshError
          ? `${refreshError} | ${boundaryMessage}`
          : boundaryMessage;
      }

      const loadedMembers =
        hydrationBoundary.acceptedRows.map(rowToMember);

      // Viewer-resolution evidence is intentionally separate from
      // production Tree hydration. It may help recover the viewer ID only
      // when the evidence row belongs to the same effective graph.
      const viewerEvidenceRows =
        ((userMemRes.data ?? []) as any[]).filter(
          row =>
            row?.owner_user_id === targetOwner,
        );

      if (!isCurrentRefresh()) {
        return;
      }

      const currentEvidenceContext =
        externalEvidenceContextRef.current;

      const evidenceRefreshReason =
        evidenceOwnerChanged
          ? 'GRAPH-OWNER-CHANGED'
          : currentEvidenceContext.snapshot.status ===
              'UNAVAILABLE'
            ? 'INITIAL-GRAPH-LOAD'
            : 'MANUAL-REFRESH';

      const evidenceLoad =
        await loadCanonicalTreeExternalEvidenceSnapshot({
          supabase,
          ownerUserId:
            targetOwner,
          members:
            loadedMembers,
          refreshReason:
            evidenceRefreshReason,
        });

      if (!isCurrentRefresh()) {
        return;
      }

      commitExternalEvidenceContext(
        adoptCanonicalTreeExternalEvidenceSnapshot({
          current:
            currentEvidenceContext,
          ownerUserId:
            targetOwner,
          members:
            loadedMembers,
          candidate:
            evidenceLoad.snapshot,
          gatewayTransportEnabled:
            false,
        }),
      );

      setMembers(loadedMembers);
      setLastFetchedCount(loadedMembers.length);
      setMemories((memoRes.data ?? []).map(rowToMemory));
      setPhotos((photoRes.data ?? []).map(rowToPhoto));
      setComments((commentRes.data ?? []).map(rowToComment));



      // Re-derive viewer id from the loaded set as the AUTHORITATIVE source
      // of truth. The graph just returned every row this user can see, so
      // whatever row is `claimed_by_user_id = user.id` in that set IS the
      // viewer's bound row — period.
      //
      // HARDENED (May 2026 — "Tree Center Diagnostic stuck on linked=—"):
      //   Previously this block only called setViewerMemberId when the
      //   loaded claimed row's id DIFFERED from the early-set viewerId.
      //   If `resolveEffectiveOwner` returned viewerId=null (the
      //   cross-graph claim SELECT was RLS-blocked, or the network round
      //   trip timed out) but the loaded `members` list DOES contain the
      //   claimed row (because the user can read it via their own owner
      //   graph OR because they're an admin/owner with broader visibility),
      //   the earlier `setViewerMemberId(viewerId)` at the top of refresh
      //   had already clobbered any previously-good value to null. Then
      //   the "claimedHere && claimedHere.id !== viewerId" guard meant
      //   we never wrote the correct id back — the user stayed stuck
      //   with viewerMemberId=null and bindingState='loading' forever.
      //
      //   The fix below ALWAYS writes the authoritative id (or null)
      //   based on what we actually loaded — no diff guard. It is safe
      //   to call setViewerMemberId with the same value React already
      //   holds; React bails out at the reconciler level. But when the
      //   value DIFFERS (the bug case), we now always converge to the
      //   loaded-claimed row's id on the very next render.
      let resolvedViewerId: string | null = viewerId;
      // ── BROADENED CANONICAL LOOKUP (May 2026 — SQL-truth alignment) ──
      // SQL proves the row is "mine" when either:
      //   claimed_by_user_id = authUserId
      // OR
      //   (owner_user_id = authUserId AND profile_state = 'claimed').
      //
      // The previous predicate only matched the first clause, so a
      // hydration race that briefly returned claimedByUserId=null on a
      // freshly-claimed row (mid-write optimistic state, partial RLS
      // column visibility on a cross-graph SELECT, or a stale members[]
      // snapshot rolling into the next refresh) left viewerMemberId
      // null even though SQL clearly identified the viewer via the
      // OR-clause. Adding the second clause closes that gap WITHOUT
      // touching schema, RLS, claims, or any write path. Pure read-
      // side reconciliation.
      const loadedClaimedHere =
        loadedMembers.find(
          member =>
            member.claimedByUserId ===
            user.id,
        ) ??
        null;

      const viewerEvidenceClaim =
        viewerEvidenceRows.find(
          row =>
            row?.owner_user_id ===
              targetOwner &&
            (
              row?.claimed_by_user_id ===
                user.id ||
              (
                targetOwner === user.id &&
                row?.profile_state ===
                  'claimed'
              )
            ),
        ) ??
        null;

      const claimedHere =
        loadedClaimedHere ??
        (
          viewerEvidenceClaim
            ? loadedMembers.find(
                member =>
                  member.id ===
                    viewerEvidenceClaim.id,
              ) ??
              null
            : null
        );
      if (claimedHere) {
        resolvedViewerId = claimedHere.id;
        setViewerMemberId(claimedHere.id);
      } else if (viewerId) {
        // viewerId from resolveEffectiveOwner pointed at a row no longer
        // claimed by us in the loaded graph — clear it.
        resolvedViewerId = null;
        setViewerMemberId(null);
      }


      // ─── VIEWER → FAMILY MEMBER MAPPING REPAIR (GLOBAL — paid invitees) ─
      // CRITICAL FIX (May 2026 — invited paid users centered on inviter):
      //   Previously the ladder could assign viewerMemberId to ANOTHER
      //   USER'S already-claimed self-anchor row (strategy d fired even
      //   when the only side='self' row was the inviter's own claimed
      //   anchor — the .is('claimed_by_user_id', null) DB guard blocked
      //   the write but the local state still set viewerMemberId to the
      //   inviter's row, centering Jane's tree on the inviter and labeling
      //   the inviter "You").
      //
      // The hardened ladder below:
      //   • NEVER assigns viewerMemberId to a row whose claimed_by_user_id
      //     is set to anyone other than the current auth user.
      //   • Adds an explicit NAME-MATCH strategy that finds an UNCLAIMED
      //     placeholder in the loaded graph whose name token-matches the
      //     auth user's display name (from profiles.name → user_metadata
      //     name/full_name → email local-part). Highest-confidence match
      //     is auto-claimed for paid/authenticated users.
      //   • Auto-claim is gated on `.is('claimed_by_user_id', null)` so
      //     it can NEVER overwrite another user's claim — the DB unique
      //     partial index on (claimed_by_user_id) WHERE NOT NULL is the
      //     hard backstop.
      //   • Emits a structured diagnostic line on every refresh so the
      //     four required fields (logged_in_user_id / linked_person_id /
      //     tree_center_person_id / is_center_correct) are always visible
      //     in the browser console.
      //
      // Pure additive repair — never mutates parent_ids, never deletes
      // any row, never touches billing / FamousPay / auth / Stripe /
      // spouse engine / kinship engine / uploads / RLS / schema. The
      // repair writes ONLY claim metadata on a single row.

      // Resolve the auth user's display name for the name-match strategy.
      let authDisplayName: string | null = null;
      try {
        const { data: prof } = await supabase
          .from('profiles')
          .select('name')
          .eq('id', user.id)
          .maybeSingle();

        if (!isCurrentRefresh()) {
          return;
        }

        authDisplayName =
          ((prof as any)?.name ?? null) ||
          ((user as any)?.user_metadata?.name ?? null) ||
          ((user as any)?.user_metadata?.full_name ?? null) ||
          (user.email ? user.email.split('@')[0] : null) ||
          null;
      } catch {
        authDisplayName =
          ((user as any)?.user_metadata?.name ?? null) ||
          ((user as any)?.user_metadata?.full_name ?? null) ||
          (user.email ? user.email.split('@')[0] : null) ||
          null;
      }

      const normalize = (s: string | null | undefined): string =>
        (s ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
      const nameTokens = (s: string | null | undefined): string[] =>
        normalize(s).split(' ').filter((t) => t.length >= 2);

      if (!resolvedViewerId) {
        const isSelfPlaceholder = (m: GraphMember) =>
          m.side === 'self' &&
          m.approvalStatus !== 'rejected' &&
          (m.relationship === 'unknown' || (m.relationship as string) === 'self');

        // Only rows that are NOT claimed by someone else are eligible to
        // become this viewer's anchor. Already-claimed-by-someone-else
        // rows are excluded GLOBALLY — they can never be the viewer's
        // center, no matter which strategy matches them.
        const notClaimedByOther = (m: GraphMember) =>
          !m.claimedByUserId || m.claimedByUserId === user.id;

        let candidate: GraphMember | null = null;
        let strategy: string | null = null;

        // (a) Graph-owner identity match — only when the viewer OWNS the
        //     loaded graph (i.e. legacy "I built my own tree" path).
        //     Invited users never satisfy this branch.
        if (targetOwner === user.id) {
          const ownerSelfRows = loadedMembers
            .filter(
              (m) =>
                m.ownerUserId === user.id &&
                isSelfPlaceholder(m) &&
                notClaimedByOther(m),
            )
            .sort(
              (a, b) =>
                new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
            );
          if (ownerSelfRows.length > 0) {
            candidate = ownerSelfRows[0];
            strategy = 'graph-owner-self-anchor';
          }
        }

        // (b) NAME-MATCH strategy — find an UNCLAIMED placeholder in the
        //     loaded (inviter's) graph whose name token-matches the auth
        //     user's display name. This is the dominant path for invited
        //     paid users: the inviter already created a placeholder for
        //     them (e.g. "Jane Smith") in the inviter's tree, and Jane
        //     now signs in. The placeholder is unclaimed → we claim it
        //     for Jane.
        if (!candidate && authDisplayName) {
          const probeTokens = nameTokens(authDisplayName);
          if (probeTokens.length >= 1) {
            const probeNorm = normalize(authDisplayName);
            const probeFirst = probeTokens[0];
            const probeLast = probeTokens[probeTokens.length - 1];

            const scoreFor = (m: GraphMember): number => {
              const mNorm = normalize(m.name);
              if (!mNorm) return 0;
              if (mNorm === probeNorm) return 100;
              const t = nameTokens(m.name);
              if (t.length === 0) return 0;
              const first = t[0];
              const last = t[t.length - 1];
              // First AND last name agree → strong match.
              if (probeTokens.length >= 2 && first === probeFirst && last === probeLast) {
                return 80;
              }
              // First-name only or last-name only → weak match — only
              // accept when EXACTLY one such row exists in the graph
              // (we check uniqueness below by counting eligible matches).
              if (first === probeFirst) return 40;
              if (last === probeLast) return 30;
              return 0;
            };

            const ranked = loadedMembers
              .filter((m) => m.approvalStatus !== 'rejected')
              .filter(notClaimedByOther)
              .filter((m) => !m.claimedByUserId) // unclaimed only — never steal
              .map((m) => ({ m, score: scoreFor(m) }))
              .filter((x) => x.score > 0)
              .sort((a, b) => b.score - a.score);

            if (ranked.length > 0) {
              const top = ranked[0];
              // Auto-bind only when the top match is unambiguous:
              //   • exact full-name OR strong first+last match, OR
              //   • the ONLY match in the graph (uniqueness).
              const isStrong = top.score >= 80;
              const isUnique = ranked.length === 1;
              if (isStrong || isUnique) {
                candidate = top.m;
                strategy = 'invited-name-match';
              } else {
                // Multiple weak matches — leave for IdentityBindingModal.
                // Surface them via viewerCandidates below.
                setViewerCandidates(ranked.slice(0, 6).map((x) => x.m));
              }
            }
          }
        }

        // (c) Created-by match — placeholder created by THIS auth user
        //     (legacy "I built my own anchor row" path). Skip if already
        //     covered by (a). Unclaimed-only.
        if (!candidate) {
          const created = loadedMembers
            .filter(
              (m) =>
                isSelfPlaceholder(m) &&
                notClaimedByOther(m) &&
                !m.claimedByUserId &&
                (m.createdBy === user.id || m.ownerUserId === user.id),
            )
            .sort(
              (a, b) =>
                new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
            );
          if (created.length > 0) {
            candidate = created[0];
            strategy = 'created-by-self-anchor';
          }
        }

        // (d) Single-root fallback — ONLY when the loaded graph has
        //     exactly one UNCLAIMED side='self' row. This guards against
        //     the previous bug where the inviter's already-claimed self
        //     row was being picked as Jane's anchor.
        if (!candidate) {
          const unclaimedSelf = loadedMembers.filter(
            (m) => isSelfPlaceholder(m) && !m.claimedByUserId,
          );
          if (unclaimedSelf.length === 1) {
            candidate = unclaimedSelf[0];
            strategy = 'single-unclaimed-root-fallback';
          }
        }

        if (candidate) {
          // CC-TREE-LIVE-003G3H: correlation may identify a candidate but
          // may never establish authenticated canonical-person identity.
          setViewerCandidates([candidate]);
          if (strategy) {
            console.info('[KinsCircle] viewer identity candidate requires governed binding', {
              strategy,
              candidateId: candidate.id,
            });
          }
        }

        // GLOBAL DIAGNOSTIC — emit the four required fields on every
        // refresh so support can verify centering correctness in any
        // production session via the browser console (filter for
        // "[KinsCircle][TreeCenterDiag]"). Pure read — never mutates.
        const linkedPerson = resolvedViewerId
          ? loadedMembers.find((m) => m.id === resolvedViewerId) ?? null
          : null;
        // Tree center = whichever row resolveViewerAnchor will pick in
        // the renderer. We mirror that logic here for the diagnostic.
        let treeCenterId: string | null = resolvedViewerId;
        if (!treeCenterId) {
          const fallbackAnchor =
            loadedMembers.find(
              (m) =>
                m.side === 'self' &&
                (m.relationship === 'unknown' ||
                  (m.relationship as string) === 'self'),
            ) ?? null;
          treeCenterId = fallbackAnchor?.id ?? null;
        }
        const isCenterCorrect = !!(
          resolvedViewerId &&
          treeCenterId &&
          resolvedViewerId === treeCenterId &&
          linkedPerson &&
          linkedPerson.claimedByUserId === user.id
        );
        const diag = {
          logged_in_user_id: user.id,
          linked_person_id: resolvedViewerId,
          tree_center_person_id: treeCenterId,
          is_center_correct: isCenterCorrect,
          // Helpful context — not part of the strict 4-field contract.
          graph_owner_user_id: targetOwner,
          auth_email: user.email ?? null,
          auth_display_name: authDisplayName,
          loaded_member_count: loadedMembers.length,
        };
        // eslint-disable-next-line no-console
        console.log('[KinsCircle][TreeCenterDiag]', diag);
        try {
          if (typeof window !== 'undefined') {
            (window as any).__KC_TREE_CENTER_DIAG__ = diag;
          }
        } catch {
          /* non-fatal */
        }

        if (!resolvedViewerId && !candidate) {
          // eslint-disable-next-line no-console
          console.warn(
            '[KinsCircle] viewer-mapping repair: no candidate row found',
            {
              authUid: user.id,
              authEmail: user.email,
              authDisplayName,
              graphOwner: targetOwner,
              memberCount: loadedMembers.length,
              selfRowCount: loadedMembers.filter(isSelfPlaceholder).length,
              unclaimedSelfRowCount: loadedMembers.filter(
                (m) => isSelfPlaceholder(m) && !m.claimedByUserId,
              ).length,
            },
          );
        }
      } else {
        // Already bound — still emit the diagnostic so every session
        // has a fresh record.
        const linkedPerson =
          loadedMembers.find((m) => m.id === resolvedViewerId) ?? null;
        const isCenterCorrect = !!(
          linkedPerson && linkedPerson.claimedByUserId === user.id
        );
        const diag = {
          logged_in_user_id: user.id,
          linked_person_id: resolvedViewerId,
          tree_center_person_id: resolvedViewerId,
          is_center_correct: isCenterCorrect,
          graph_owner_user_id: targetOwner,
          auth_email: user.email ?? null,
          auth_display_name: authDisplayName,
          loaded_member_count: loadedMembers.length,
        };
        // eslint-disable-next-line no-console
        console.log('[KinsCircle][TreeCenterDiag]', diag);
        try {
          if (typeof window !== 'undefined') {
            (window as any).__KC_TREE_CENTER_DIAG__ = diag;
          }
        } catch {
          /* non-fatal */
        }
      }
    } catch (err: any) {
      console.error('[FamilyGraph] load failed', err);
      refreshError = refreshError
        ? `${refreshError} | outer: ${err?.message ?? String(err)}`
        : `outer: ${err?.message ?? String(err)}`;
    } finally {
      // Obsolete refreshes must not overwrite newer diagnostics or clear
      // loading state owned by the current refresh generation.
      if (isCurrentRefresh()) {
        setLastRefreshError(refreshError);
        setLastTargetOwner(targetOwner);
        setLoading(false);
      }
    }
  }, [user?.id, user?.email, resolveEffectiveOwner]);




  // ─── Debounced refresh ────────────────────────────────────────────────
  // Coalesces multiple `scheduleRefresh()` calls within ~180ms into ONE
  // `refresh()` invocation. Used by `runBulkAdd` and by save flows that
  // would otherwise issue back-to-back refreshes (e.g. AddGraphMemberModal
  // saving Mom then immediately Dad). The debounced timer is cleared on
  // unmount so we never call refresh on an unmounted provider.
  const scheduleRefresh = useCallback(() => {
    refreshPendingRef.current = true;
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    refreshTimerRef.current = setTimeout(() => {
      refreshTimerRef.current = null;
      const wasPending = refreshPendingRef.current;
      refreshPendingRef.current = false;
      if (!wasPending) return;
      // If a bulk batch is still open, defer the refresh until it closes.
      // runBulkAdd's finally-block will re-call scheduleRefresh once
      // depth returns to zero, so no refresh ever gets dropped.
      if (bulkDepthRef.current > 0) {
        refreshPendingRef.current = true;
        return;
      }
      refresh().catch((err) => {
        console.warn('[FamilyGraph] debounced refresh failed', err);
      });
    }, 180);
  }, [refresh]);

  // Clear any pending refresh timer on unmount.
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, []);

  // ─── Bulk-add wrapper (HARDENED) ──────────────────────────────────────
  // Increments the bulk depth counter, runs `fn`, decrements on exit, and
  // when the depth returns to zero IMMEDIATELY flips `isBulkWriting=false`
  // and schedules a single coalesced refresh. The previous 220ms deferred
  // flip could theoretically leave the layout-lock stuck on if the user
  // unmounted the tree mid-batch (the timer would fire after unmount,
  // see no depth change, and the next mount would inherit the stale
  // true). The hardened version keeps the lock window precisely bounded
  // by the synchronous try/finally, so it can never persist past `fn`.
  // Re-entrant: nested `runBulkAdd` calls share one batch boundary.
  // Errors thrown by `fn` propagate to the caller after the depth has
  // been correctly decremented. Reads `isBulkWriting` via a ref-free
  // setter form so the callback identity stays stable across renders
  // (no stale-closure risk).
  const runBulkAdd = useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T> => {
      bulkDepthRef.current += 1;
      setIsBulkWriting(true);
      try {
        return await fn();
      } finally {
        bulkDepthRef.current = Math.max(0, bulkDepthRef.current - 1);
        if (bulkDepthRef.current === 0) {
          // Trailing single refresh — coalesces into one round-trip
          // even if multiple runBulkAdd batches just closed.
          scheduleRefresh();
          // Release the layout lock immediately. Any consumer reading
          // `isBulkWriting` (e.g. the tree's auto-fit gate) recomputes
          // exactly once on the next render with the final bounds.
          setIsBulkWriting(false);
        }
      }
    },
    [scheduleRefresh],
  );



  useEffect(() => {
    if (isAuthenticated) {
      refresh();
    } else {
      // Logout invalidates every graph refresh still in flight.
      refreshGenerationRef.current += 1;

      setMembers([]);
      setMemories([]);
      setPhotos([]);
      setComments([]);
      setEffectiveOwnerUserId(null);
      setViewerMemberId(null);
      setLastRefreshError(null);
      setLastTargetOwner(null);
      setLastFetchedCount(0);
      setLoading(false);

      commitExternalEvidenceContext(
        createCanonicalTreeExternalEvidenceContextState({
          gatewayTransportEnabled: false,
        }),
      );
    }
  }, [isAuthenticated, refresh]);

  // ─── Cross-component refresh signal ─────────────────────────────────────
  // External modules (e.g. RelationshipOnboardingModal after the invite-to-
  // account binding completes) dispatch a `kc:family-graph-refresh` window
  // event when they need every active FamilyGraphProvider to re-resolve
  // the effective owner and re-fetch members. This is what guarantees
  // that an invited user's freshly-claimed row is visible (and the
  // duplicate "YOU" root is gone) WITHOUT a page reload.
  useEffect(() => {
    if (!isAuthenticated) return;
    const handler = () => {
      refresh().catch(() => {
        /* non-fatal — next nav/refresh will retry */
      });
    };
    window.addEventListener('kc:family-graph-refresh', handler);
    return () => window.removeEventListener('kc:family-graph-refresh', handler);
  }, [isAuthenticated, refresh]);


  const addMember = useCallback<FamilyGraphContextType['addMember']>(
    async (input) => {
      if (!user?.id) throw new Error('Sign in required to add a family member.');
      // ─── ROBUST ADD-MEMBER WRITE PIPELINE ──────────────────────────────
      // CRITICAL BUG FIX (silent failures producing empty success toasts):
      //   The previous version of this function would happily return
      //   `null` when Supabase's `.select('*').maybeSingle()` resolved
      //   to data=null (which can happen when RLS allows INSERT but
      //   filters the post-insert SELECT, when the row was created but
      //   not returned, or when the network response was truncated).
      //   Returning null caused AddGraphMemberModal to close with a
      //   success toast even though nothing visibly happened — exactly
      //   the "fails silently, person doesn't appear" symptom the user
      //   reported.
      //
      //   The hardened pipeline now:
      //     1. Inserts the row WITHOUT chaining `.select()`. We always
      //        get a clean error/no-error signal first.
      //     2. If the insert errored, throw the original error so the
      //        modal's catch block surfaces a visible toast.
      //     3. Re-fetch the just-inserted row by name + owner + created
      //        timestamp range as a fallback when the optimistic select
      //        misses. This guarantees the caller receives a real,
      //        DB-confirmed row whenever the insert actually committed.
      //     4. If we still cannot find any row after all fallbacks
      //        (truly silent failure path — RLS rejection on select,
      //        network anomaly, etc.), throw a clearly-labeled error
      //        so the UI never tells the user "saved" when nothing was.
      //
      // Pure save-pipeline robustness — parent_ids semantics, billing,
      // FamousPay, auth, invites, uploads, Resend, onboarding, Feature
      // Guide, Health Panel, and the database schema are NEVER touched.
      return enqueueWrite(async () => {
        const ownerForWrite = effectiveOwnerUserId ?? user.id;

        // ─── BRANCH-SCOPE AUTO-TAGGING (Nov 2026 — global branch isolation) ──
        // When a CLAIMED family member (not the graph owner) adds a new
        // relative whose parent_ids do NOT trace back to the graph
        // owner's own bloodline, the new row gets tagged with
        // `metadata.branch_owner_member_id = <actor's viewer member id>`.
        // Centered-tree views then hide the row from anyone except the
        // branch owner themselves AND their descendants — preventing
        // a claimed member's in-laws / other-parent-side relatives from
        // polluting the graph owner's centered view. Pure additive:
        // parent_ids / spouse_id / kinship engine / RLS / schema are
        // unchanged, and rows without the tag behave exactly as before.
        //
        // Auto-tagging fires only when ALL of the following hold:
        //   • The actor (user.id) is NOT the graph owner (ownerForWrite).
        //   • The actor has a known viewerMemberId (their claimed row).
        //   • Input did not already set branchOwnerMemberId.
        //   • The new row's parent_ids do not intersect the graph owner's
        //     own bloodline closure (computed from the local members
        //     snapshot — best-effort; misses fall through to "visible").
        let resolvedBranchOwnerId: string | null =
          input.branchOwnerMemberId ?? null;
        if (
          resolvedBranchOwnerId === null &&
          viewerMemberId &&
          ownerForWrite !== user.id
        ) {
          // Find the graph-owner's canonical anchor row (claimed by owner
          // OR an unclaimed self-anchor placeholder owned by them).
          const ownerAnchor =
            members.find(
              (m) =>
                m.ownerUserId === ownerForWrite &&
                (m.claimedByUserId === ownerForWrite ||
                  (m.side === 'self' &&
                    (m.relationship === 'unknown' ||
                      (m.relationship as string) === 'self'))),
            ) ?? null;
          if (ownerAnchor) {
            // Compute owner's bloodline = ancestors + descendants of the
            // owner anchor via parent_ids. Any new-row parent_ids that
            // overlap with this set count as "connected to the main
            // tree" → no auto-tag. Otherwise auto-tag.
            const byId = new Map(members.map((m) => [m.id, m]));
            const bloodline = new Set<string>([ownerAnchor.id]);
            // Walk ancestors. CYCLE-SAFE: `bloodline.has(pid)` skip ensures
            // each ancestor id is processed at most once even if parent_ids
            // contains a malformed cycle. Visit cap is a defensive secondary
            // bound to guarantee we cannot blow the call stack on a
            // pathological graph.
            const upQueue: string[] = [ownerAnchor.id];
            let upVisits = 0;
            while (upQueue.length) {
              if (upVisits++ > 50_000) {
                // eslint-disable-next-line no-console
                console.warn('[FamilyGraph] addMember ancestor walk visit cap hit');
                break;
              }
              const cur = upQueue.shift()!;
              const m = byId.get(cur);
              if (!m) continue;
              for (const pid of m.parentIds ?? []) {
                if (!pid || bloodline.has(pid) || !byId.has(pid)) continue;
                bloodline.add(pid);
                upQueue.push(pid);
              }
            }
            // Walk descendants (transitive closure downward).
            // CYCLE-SAFE: the fixed-point loop is bounded by members.length+1
            // since each pass that flips `changed=true` adds at least one new
            // id to the bloodline set.
            let changed = true;
            let downIter = 0;
            const downMaxIter = members.length + 2;
            while (changed) {
              if (downIter++ > downMaxIter) {
                // eslint-disable-next-line no-console
                console.warn('[FamilyGraph] addMember descendant closure iter cap hit');
                break;
              }
              changed = false;
              for (const m of members) {
                if (bloodline.has(m.id)) continue;
                for (const pid of m.parentIds ?? []) {
                  if (pid && bloodline.has(pid)) {
                    bloodline.add(m.id);
                    changed = true;
                    break;
                  }
                }
              }
            }

            const newParentIds = Array.isArray(input.parentIds)
              ? cleanParentIds(input.parentIds)
              : [];
            const touchesBloodline = newParentIds.some((pid) =>
              bloodline.has(pid),
            );
            if (!touchesBloodline) {
              resolvedBranchOwnerId = viewerMemberId;
            }
          }
        }

        const row: Record<string, any> = {
          owner_user_id: ownerForWrite,
          name: input.name.trim(),
          nickname: input.nickname ?? null,
          relationship: input.relationship,
          side: input.side,
          photo_url: input.photoUrl ?? null,
          birth_year: input.birthYear ?? null,
          birth_date: input.birthDate ?? null,
          birth_month: input.birthMonth ?? null,
          birth_day: input.birthDay ?? null,
          birthday_visibility: input.birthdayVisibility ?? 'public',
          birthday_reminders: input.birthdayReminders ?? true,
          living_status: input.livingStatus ?? null,
          notes: input.notes ?? null,
          city: input.city ?? null,
          description: input.description ?? null,
          approval_status: input.approvalStatus ?? 'pending',
          created_by: user.id,
        };
        if (Array.isArray(input.parentIds) && input.parentIds.length > 0) {
          row.parent_ids = cleanParentIds(input.parentIds);
        }
        if (input.spouseId) row.spouse_id = input.spouseId;
        if (input.relationshipOverride && input.relationshipOverride.trim()) {
          row.relationship_override = input.relationshipOverride.trim();
        }
        // Persist the resolved branch-scope tag (when present) into the
        // existing `metadata` jsonb column. Null/missing keeps the row
        // visible to everyone in the graph (legacy behaviour).
        const metadata: Record<string, unknown> = {};

        if (resolvedBranchOwnerId) {
          metadata.branch_owner_member_id = resolvedBranchOwnerId;
        }

        if (input.relationshipWorkflow) {
          metadata.relationship_workflow =
            input.relationshipWorkflow;
        }

        if (Object.keys(metadata).length > 0) {
          row.metadata = metadata;
        }


        const result =
          await gatewayInsertMember(
            {
              supabase,
              actingUserId:
                user.id,
              members,
              viewerMemberId,
              source:
                'add-graph-member',
            },
            {
              ownerUserId:
                ownerForWrite,
              name:
                input.name.trim(),
              relationship:
                input.relationship,
              side:
                input.side,
              row,
              birthYear:
                input.birthYear ?? null,
              birthDate:
                input.birthDate ?? null,
              parentIds:
                Array.isArray(
                  input.parentIds,
                )
                  ? cleanParentIds(
                      input.parentIds,
                    )
                  : [],
              spouseId:
                input.spouseId ?? null,
            },
          );

        if (!result.ok) {
          throw new Error(
            result.reason ||
              'The Tree mutation gateway blocked this family member insert.',
          );
        }

        const insertedRow =
          result.data.row;

        if (!insertedRow) {
          throw new Error(
            `Could not save ${input.name.trim()} — the database did not confirm the new row. Please try again.`,
          );
        }

        const member =
          rowToMember(insertedRow);

        setMembers((previous) =>
          previous.some(
            existing =>
              existing.id === member.id,
          )
            ? previous
            : [...previous, member],
        );

        invalidateExternalEvidence(
          'MEMBERS-CHANGED',
        );

        return member;
      });
    },
    [user?.id, effectiveOwnerUserId, enqueueWrite, members, viewerMemberId]
  );




  const updateMember = useCallback<FamilyGraphContextType['updateMember']>(
    async (id, updates) => {
      // Serialize through the write queue so an updateMember (parent_ids,
      // spouse_id, name, photo, etc.) can never interleave with a sibling
      // addMember / linkParents / linkSpouses on the same row. This is
      // what eliminates the "I added Mom, then Dad, then a grandfather
      // and the grandfather attached to the wrong parent" race during
      // rapid onboarding sequences.
      return enqueueWrite(async () => {
        const row: Record<string, any> = { updated_at: new Date().toISOString() };
        if (updates.name !== undefined) row.name = updates.name;
        if (updates.nickname !== undefined) row.nickname = updates.nickname;
        if (updates.relationship !== undefined) row.relationship = updates.relationship;
        if (updates.side !== undefined) row.side = updates.side;
        if (updates.photoUrl !== undefined) row.photo_url = updates.photoUrl;
        if (updates.birthYear !== undefined) row.birth_year = updates.birthYear;
        if (updates.birthDate !== undefined) row.birth_date = updates.birthDate;
        if (updates.birthMonth !== undefined) row.birth_month = updates.birthMonth;
        if (updates.birthDay !== undefined) row.birth_day = updates.birthDay;
        if (updates.birthdayVisibility !== undefined) row.birthday_visibility = updates.birthdayVisibility;
        if (updates.birthdayReminders !== undefined) row.birthday_reminders = updates.birthdayReminders;
        if (updates.livingStatus !== undefined) row.living_status = updates.livingStatus;
        if (updates.notes !== undefined) row.notes = updates.notes;
        if (updates.city !== undefined) row.city = updates.city;
        if (updates.description !== undefined) row.description = updates.description;
        if (updates.approvalStatus !== undefined) row.approval_status = updates.approvalStatus;
        if (updates.confirmationsCount !== undefined) row.confirmations_count = updates.confirmationsCount;
        if (updates.relationshipOverride !== undefined) row.relationship_override = updates.relationshipOverride;

        if (
          updates.branchOwnerMemberId !== undefined ||
          updates.relationshipWorkflow !== undefined ||
          updates.treeNamePreference !== undefined
        ) {
          const current = members.find((member) => member.id === id);

          const metadata: Record<string, unknown> = {};

          const branchOwner =
            updates.branchOwnerMemberId !== undefined
              ? updates.branchOwnerMemberId
              : current?.branchOwnerMemberId ?? null;

          const relationshipWorkflow =
            updates.relationshipWorkflow !== undefined
              ? updates.relationshipWorkflow
              : current?.relationshipWorkflow ?? null;

          const treeNamePreference =
            updates.treeNamePreference !== undefined
              ? updates.treeNamePreference
              : current?.treeNamePreference ??
                'real-name';

          if (branchOwner) {
            metadata.branch_owner_member_id = branchOwner;
          }

          if (relationshipWorkflow) {
            metadata.relationship_workflow =
              relationshipWorkflow;
          }

          metadata.tree_name_preference =
            treeNamePreference;

          row.metadata = metadata;
        }

        const result =
          await gatewayUpdateMember(
            {
              supabase,
              actingUserId:
                user?.id ?? null,
              members,
              viewerMemberId,
              source:
                'add-graph-member',
            },
            {
              id,
              row,
            },
          );

        if (!result.ok) {
          throw new Error(
            result.reason ||
              'The Tree mutation gateway blocked this member update.',
          );
        }

        setMembers((prev) =>
          prev.map((m) =>
            m.id === id
              ? { ...m, ...updates }
              : m,
          ),
        );

        const changesRelationshipEvidence =
          updates.relationship !== undefined ||
          updates.side !== undefined ||
          updates.parentIds !== undefined ||
          updates.spouseId !== undefined ||
          updates.relationshipOverride !== undefined ||
          updates.branchOwnerMemberId !== undefined ||
          updates.relationshipWorkflow !== undefined ||
          updates.profileState !== undefined ||
          updates.claimedByUserId !== undefined;

        if (changesRelationshipEvidence) {
          invalidateExternalEvidence(
            'MEMBERS-CHANGED',
          );
        }
      });
    },
    [enqueueWrite, members, user?.id]
  );





  const approveMember = useCallback(
    async (id: string) => updateMember(id, { approvalStatus: 'approved' }),
    [updateMember]
  );
  const rejectMember = useCallback(
    async (id: string) => updateMember(id, { approvalStatus: 'rejected' }),
    [updateMember]
  );

  const confirmMember = useCallback(
    async (id: string) => {
      if (!user?.id) throw new Error('Sign in required.');
      const target = members.find((m) => m.id === id);
      if (!target) return;
      const { error: insertErr } = await supabase
        .from('family_graph_confirmations')
        .insert({ member_id: id, confirmed_by: user.id });
      if (insertErr && !`${insertErr.message}`.toLowerCase().includes('duplicate')) {
        throw insertErr;
      }
      const { count } = await supabase
        .from('family_graph_confirmations')
        .select('*', { count: 'exact', head: true })
        .eq('member_id', id);
      const newCount = count ?? target.confirmationsCount + 1;
      const shouldAutoApprove =
        target.approvalStatus !== 'approved' && newCount >= 2;
      const updates: Partial<GraphMember> = { confirmationsCount: newCount };
      if (shouldAutoApprove) updates.approvalStatus = 'approved';
      await updateMember(id, updates);
    },
    [user?.id, members, updateMember]
  );

  const deleteMember = useCallback(
    async (
      request: GatewayDeleteMemberRequest,
    ): Promise<void> => {
      if (!user?.id) {
        throw new Error(
          'Sign in required.',
        );
      }

      const target =
        members.find(
          member =>
            member.id ===
            request.memberId,
        );

      if (!target) {
        throw new Error(
          'Family member not found.',
        );
      }

      const result =
        await gatewayDeleteMember(
          {
            supabase,
            actingUserId:
              user.id,
            members,
            viewerMemberId,
            source:
              'add-graph-member',
          },
          request,
        );

      if (!result.ok) {
        throw new Error(
          result.reason ||
            'Could not remove this erroneous family record.',
        );
      }

      setMembers(
        previous =>
          previous.filter(
            member =>
              member.id !==
              request.memberId,
          ),
      );

      invalidateExternalEvidence(
        'MEMBERS-CHANGED',
      );
    },
    [
      members,
      user?.id,
      viewerMemberId,
    ],
  );

  const rollbackCreatedMember = useCallback(
    async (
      member: GraphMember,
      explanation?: string | null,
    ): Promise<void> => {
      if (!user?.id) {
        throw new Error(
          'Sign in required for rollback cleanup.',
        );
      }

      const rollbackMembers =
        members.some(
          current =>
            current.id === member.id,
        )
          ? members
          : [...members, member];

      const result =
        await gatewayRollbackCreatedMember(
          {
            supabase,
            actingUserId:
              user.id,
            members:
              rollbackMembers,
            viewerMemberId,
            source:
              'add-relative-rollback',
          },
          {
            memberId:
              member.id,
            errorClass:
              'FAILED_CREATION',
            explanation:
              (
                explanation ??
                'Rolled back a newly created erroneous record during the controlled add-relative workflow.'
              ).trim(),
          },
        );

      if (!result.ok) {
        throw new Error(
          result.reason ||
            'The Tree mutation gateway blocked rollback cleanup.',
        );
      }

      setMembers(
        previous =>
          previous.filter(
            current =>
              current.id !== member.id,
          ),
      );

      invalidateExternalEvidence(
        'MEMBERS-CHANGED',
      );
    },
    [
      members,
      user?.id,
      viewerMemberId,
    ],
  );

  const restoreMember = useCallback(
    async (id: string): Promise<void> => {
      if (!user?.id) {
        throw new Error('Sign in required.');
      }

      const result = await gatewayRestoreMember(
        {
          supabase,
          actingUserId: user.id,
          members,
          viewerMemberId,
          source: 'add-graph-member',
        },
        id,
      );

      if (!result.ok) {
        throw new Error(
          result.reason ||
          'Could not restore this family member.',
        );
      }

      invalidateExternalEvidence(
        'MEMBERS-CHANGED',
      );

      await refresh();
    },
    [
      user?.id,
      members,
      refresh,
      invalidateExternalEvidence,
    ],
  );

  const addMemory = useCallback<FamilyGraphContextType['addMemory']>(
    async (memberId, input) => {
      if (!user?.id) throw new Error('Sign in required.');
      const payload: MemoryInput =
        typeof input === 'string' ? { text: input } : input;
      const trimmed = (payload.text ?? '').trim();
      if (!trimmed && !payload.title) return null;
      const ownerForWrite = effectiveOwnerUserId ?? user.id;
      const { data, error } = await supabase
        .from('family_graph_memories')
        .insert({
          member_id: memberId,
          owner_user_id: ownerForWrite,
          text: trimmed,
          title: payload.title ?? null,
          event_date: payload.eventDate ?? null,
          date_precision: payload.datePrecision ?? 'exact',
          location: payload.location ?? null,
          tagged_member_ids: payload.taggedMemberIds ?? [],
          privacy: payload.privacy ?? 'family',
          life_stage: payload.lifeStage ?? null,
          entry_kind: payload.entryKind ?? 'memory',
          created_by: user.id,
        })
        .select('*')
        .maybeSingle();
      if (error) throw error;
      if (data) {
        const mem = rowToMemory(data);
        setMemories((prev) => [mem, ...prev]);

        // ── Fire-and-forget family activity email notification.
        // Never blocks the upload; never throws. The edge function
        // resolves recipients server-side, excludes the actor, dedupes,
        // and respects each recipient's email_prefs.
        try {
          // Skip private memories — only the uploader can see them.
          if ((payload.privacy ?? 'family') !== 'private') {
            const aboutMember = members.find((m) => m.id === memberId);
            const actorMember = members.find((m) => m.claimedByUserId === user.id);
            const actorName =
              actorMember?.name ||
              (user as any)?.user_metadata?.name ||
              (user as any)?.user_metadata?.full_name ||
              user?.email?.split('@')[0] ||
              'A family member';
            const memoryKind = (payload.entryKind ?? 'memory') as
              | 'memory' | 'story' | 'life_event';
            void notifyFamilyOfMemoryUpload({
              ownerUserId: ownerForWrite,
              actorUserId: user.id,
              actorName,
              memoryKind,
              memoryTitle: payload.title ?? null,
              aboutMemberName: aboutMember?.name ?? null,
            });
          }
        } catch (notifyErr) {
          console.debug('[FamilyGraph] addMemory notify (ignored)', notifyErr);
        }

        return mem;
      }
      return null;
    },
    [user, effectiveOwnerUserId, members]
  );


  const updateMemory = useCallback<FamilyGraphContextType['updateMemory']>(
    async (id, updates) => {
      const row: Record<string, any> = {};
      if (updates.text !== undefined) row.text = updates.text;
      if (updates.title !== undefined) row.title = updates.title;
      if (updates.eventDate !== undefined) row.event_date = updates.eventDate;
      if (updates.datePrecision !== undefined) row.date_precision = updates.datePrecision;
      if (updates.location !== undefined) row.location = updates.location;
      if (updates.taggedMemberIds !== undefined) row.tagged_member_ids = updates.taggedMemberIds;
      if (updates.privacy !== undefined) row.privacy = updates.privacy;
      if (updates.lifeStage !== undefined) row.life_stage = updates.lifeStage;
      if (updates.entryKind !== undefined) row.entry_kind = updates.entryKind;
      const { error } = await supabase
        .from('family_graph_memories')
        .update(row)
        .eq('id', id);
      if (error) throw error;
      setMemories((prev) =>
        prev.map((m) => (m.id === id ? { ...m, ...updates } : m))
      );
    },
    []
  );

  const deleteMemory = useCallback(async (id: string) => {
    const { error } = await supabase.from('family_graph_memories').delete().eq('id', id);
    if (error) throw error;
    setMemories((prev) => prev.filter((m) => m.id !== id));
    setComments((prev) =>
      prev.filter((c) => !(c.entryType === 'memory' && c.entryId === id))
    );
  }, []);

  const addPhoto = useCallback<FamilyGraphContextType['addPhoto']>(
    async (memberId, url, storagePath, caption, mediaType, meta) => {
      if (!user?.id) throw new Error('Sign in required.');
      // Privacy default = 'family' (visible to claimed members of this graph),
      // matching the column default in the DB. 'private' = only_me. 'shared' =
      // selected members (shared_with_member_ids must be supplied).
      const privacy: EntryPrivacy = meta?.privacy ?? 'family';
      const sharedIds =
        privacy === 'shared'
          ? Array.from(new Set((meta?.sharedWithMemberIds ?? []).filter(Boolean)))
          : [];
      const ownerForWrite = effectiveOwnerUserId ?? user.id;
      // Subject assignment — "Who is this memory about?". Persisted in the
      // existing `tagged_member_ids` column (no schema change). We accept
      // BOTH the legacy `taggedMemberIds` and the new `subjectMemberIds`
      // alias, and write the de-duplicated UNION so callers can use either
      // vocabulary safely. If neither is provided, the column is written
      // as `[]` and the photo behaves as a general "Family memory" (only
      // visible on the anchor profile + the family-wide feed).
      const subjectIds = Array.from(
        new Set(
          [
            ...((meta?.taggedMemberIds ?? []) as string[]),
            ...((meta?.subjectMemberIds ?? []) as string[]),
          ].filter(Boolean),
        ),
      );
      const { data, error } = await supabase
        .from('family_graph_photos')
        .insert({
          member_id: memberId,
          owner_user_id: ownerForWrite,
          url,
          storage_path: storagePath ?? null,
          caption: caption ?? null,
          media_type: mediaType ?? 'image',
          title: meta?.title ?? null,
          event_date: meta?.eventDate ?? null,
          date_precision: meta?.datePrecision ?? 'exact',
          location: meta?.location ?? null,
          tagged_member_ids: subjectIds,
          privacy,
          shared_with_member_ids: sharedIds,
          created_by: user.id,
        })
        .select('*')
        .maybeSingle();
      if (error) throw error;
      if (data) {
        const p = rowToPhoto(data);
        setPhotos((prev) => [p, ...prev]);

        // ── Fire-and-forget family activity email notification.
        // Photo / video uploads — only notify when the privacy level is
        // 'family' (visible to the whole circle). Private and selectively
        // shared media skip notifications to avoid leaking content scope.
        try {
          if (privacy === 'family') {
            const aboutMember = members.find((m) => m.id === memberId);
            const actorMember = members.find((m) => m.claimedByUserId === user.id);
            const actorName =
              actorMember?.name ||
              (user as any)?.user_metadata?.name ||
              (user as any)?.user_metadata?.full_name ||
              user?.email?.split('@')[0] ||
              'A family member';
            void notifyFamilyOfMemoryUpload({
              ownerUserId: ownerForWrite,
              actorUserId: user.id,
              actorName,
              memoryKind: (mediaType === 'video' ? 'video' : 'photo'),
              memoryTitle: meta?.title ?? caption ?? null,
              aboutMemberName: aboutMember?.name ?? null,
            });
          }
        } catch (notifyErr) {
          console.debug('[FamilyGraph] addPhoto notify (ignored)', notifyErr);
        }

        return p;
      }
      return null;
    },
    [user, effectiveOwnerUserId, members]
  );


  const updatePhoto = useCallback<FamilyGraphContextType['updatePhoto']>(
    async (id, updates) => {
      const row: Record<string, any> = {};
      if (updates.caption !== undefined) row.caption = updates.caption;
      if (updates.title !== undefined) row.title = updates.title;
      if (updates.eventDate !== undefined) row.event_date = updates.eventDate;
      if (updates.datePrecision !== undefined) row.date_precision = updates.datePrecision;
      if (updates.location !== undefined) row.location = updates.location;
      // Subject assignment edits — accept either alias and persist to the
      // SAME `tagged_member_ids` column. When BOTH are passed we write the
      // union; when only one is passed it wins. This is what enables
      // "Edit who this memory is about" later without re-uploading the
      // file.
      if (
        updates.taggedMemberIds !== undefined ||
        updates.subjectMemberIds !== undefined
      ) {
        const merged = Array.from(
          new Set(
            [
              ...((updates.taggedMemberIds ?? []) as string[]),
              ...((updates.subjectMemberIds ?? []) as string[]),
            ].filter(Boolean),
          ),
        );
        row.tagged_member_ids = merged;
      }
      if (updates.privacy !== undefined) row.privacy = updates.privacy;
      const { error } = await supabase
        .from('family_graph_photos')
        .update(row)
        .eq('id', id);
      if (error) throw error;
      setPhotos((prev) =>
        prev.map((p) => {
          if (p.id !== id) return p;
          // Mirror the merged subject ids into both client-side fields so
          // the UI sees the new assignment immediately (no refetch needed).
          const merged =
            updates.taggedMemberIds !== undefined ||
            updates.subjectMemberIds !== undefined
              ? Array.from(
                  new Set(
                    [
                      ...((updates.taggedMemberIds ?? []) as string[]),
                      ...((updates.subjectMemberIds ?? []) as string[]),
                    ].filter(Boolean),
                  ),
                )
              : undefined;
          return {
            ...p,
            ...updates,
            ...(merged !== undefined
              ? { taggedMemberIds: merged, subjectMemberIds: merged }
              : {}),
          };
        }),
      );
    },
    []
  );


  const deletePhoto = useCallback<FamilyGraphContextType['deletePhoto']>(
    async (id) => {
      const target = photos.find((p) => p.id === id);
      const { error } = await supabase
        .from('family_graph_photos')
        .delete()
        .eq('id', id);
      if (error) throw error;
      if (target?.storagePath) {
        supabase.storage
          .from('family-media')
          .remove([target.storagePath])
          .catch(() => {});
      }
      setPhotos((prev) => prev.filter((p) => p.id !== id));
      setComments((prev) =>
        prev.filter((c) => !(c.entryType === 'photo' && c.entryId === id))
      );
    },
    [photos]
  );

  const addComment = useCallback<FamilyGraphContextType['addComment']>(
    async (memberId, entryType, entryId, text, authorName) => {
      if (!user?.id) throw new Error('Sign in required.');
      const trimmed = text.trim();
      if (!trimmed) return null;
      const owner =
        members.find((m) => m.id === memberId)?.ownerUserId ??
        effectiveOwnerUserId ??
        user.id;
      const { data, error } = await supabase
        .from('family_graph_memory_comments')
        .insert({
          owner_user_id: owner,
          member_id: memberId,
          entry_type: entryType,
          entry_id: entryId,
          text: trimmed,
          author_user_id: user.id,
          author_name: authorName ?? null,
        })
        .select('*')
        .maybeSingle();
      if (error) throw error;
      if (data) {
        const c = rowToComment(data);
        setComments((prev) => [...prev, c]);
        return c;
      }
      return null;
    },
    [user?.id, members, effectiveOwnerUserId]
  );

  const deleteComment = useCallback<FamilyGraphContextType['deleteComment']>(
    async (id) => {
      const { error } = await supabase
        .from('family_graph_memory_comments')
        .delete()
        .eq('id', id);
      if (error) throw error;
      setComments((prev) => prev.filter((c) => c.id !== id));
    },
    []
  );

  const findPossibleDuplicates = useCallback<FamilyGraphContextType['findPossibleDuplicates']>(
    (name, relationship, side, birthYear) => {
      // STRICT matcher — see `isStrongNameMatch` above. Requires first
      // AND last token agreement, ignores single-letter middle initials.
      // This is what stops "Earnest J. Walker" from being flagged as a
      // duplicate of "Princess J. Walker" (shared initial + shared
      // surname is no longer enough to trigger).
      const matches: GraphMember[] = [];
      for (const m of members) {
        if (m.approvalStatus === 'rejected') continue;
        const sameSide =
          m.side === side ||
          m.side === 'both' ||
          side === 'both' ||
          m.side === 'self' ||
          side === 'self';
        if (!sameSide) continue;
        const nm = isStrongNameMatch(m.name, name);
        const sameDOB = !!birthYear && !!m.birthYear && birthYear === m.birthYear;
        // Accept either a strong name match (first + last agree) OR an
        // exact birth-year match combined with at least a loose token
        // overlap (so DOB collisions don't slip through unnoticed when
        // names differ slightly — e.g. nickname vs full first name).
        const looseTokenOverlap =
          sameDOB &&
          strictTokens(m.name).some((t) => strictTokens(name).includes(t));
        if (!nm.match && !looseTokenOverlap) continue;
        // When BOTH names have a birth year, an off-by-many year mismatch
        // is a strong negative signal — likely two different people who
        // happen to share first + last name. Drop the suggestion in that
        // case so we don't pester users.
        if (birthYear && m.birthYear) {
          if (Math.abs(birthYear - m.birthYear) > 3 && !nm.exact) continue;
        }
        matches.push(m);
      }
      // Surface exact full-name + DOB matches first; then exact name;
      // then strong-name matches; then everything else.
      matches.sort((a, b) => {
        const aExactName = isStrongNameMatch(a.name, name).exact ? 0 : 1;
        const bExactName = isStrongNameMatch(b.name, name).exact ? 0 : 1;
        if (aExactName !== bExactName) return aExactName - bExactName;
        const aSameRel = a.relationship === relationship ? 0 : 1;
        const bSameRel = b.relationship === relationship ? 0 : 1;
        return aSameRel - bSameRel;
      });
      return matches;
    },
    [members]
  );


  // ---------- Relationship linking ----------
  const connectExistingAncestryAtomic =
    useCallback<
      FamilyGraphContextType[
        'connectExistingAncestryAtomic'
      ]
    >(
      async (
        request,
      ) => {
        const result =
          await gatewayConnectExistingAncestryAtomic(
            {
              supabase,

              actingUserId:
                user?.id ??
                null,

              members,

              viewerMemberId,

              source:
                'add-graph-member',
            },

            request,
          );

        if (
          'reason' in result
        ) {
          throw new Error(
            result.reason ||
              'The Tree mutation gateway blocked the atomic ancestry transaction.',
          );
        }

        setMembers(
          previous =>
            previous.map(
              member => {
                let next =
                  member;

                if (
                  member.id ===
                  result.data
                    .edgeMemberId
                ) {
                  next = {
                    ...next,

                    parentIds: [
                      ...result.data
                        .parentIds,
                    ],
                  };
                }

                if (
                  member.id ===
                  result.data
                    .evidenceMemberId
                ) {
                  next = {
                    ...next,

                    relationshipWorkflow:
                      result.data
                        .relationshipWorkflow,

                    relationshipOverride:
                      result.data
                        .relationshipOverride,
                  };
                }

                return next;
              },
            ),
        );

        invalidateExternalEvidence(
          'MEMBERS-CHANGED',
        );

        return result.data;
      },
      [
        members,
        user?.id,
        viewerMemberId,
      ],
    );


  const linkParents = useCallback<FamilyGraphContextType['linkParents']>(
    async (memberId, parentIds) => {
      // De-dup + drop self-references. We deliberately DO NOT filter by
      // "id must already exist in the local `members` closure" here:
      //
      // BUG FIX (separated/non-spouse parents — adding a Father when a
      // Mother already exists silently failed; adding a grandfather via
      // the parent-side "+" destabilised the tree):
      //   Callers (AddGraphMemberModal) often invoke linkParents IN THE
      //   SAME synchronous handler that just called addMember. addMember's
      //   setMembers update is async — React hasn't re-rendered yet — so
      //   the linkParents useCallback's `members` closure is one tick
      //   behind and does NOT yet contain the freshly-inserted row. The
      //   previous "filter unknown ids" guard would silently DROP the
      //   just-created relative's id, leaving them floating and (worse,
      //   when called in the reverse-direction grandparent path) wiping
      //   any other previously-linked parents because the filter ran on
      //   the entire next-state list.
      //
      // Trust the caller for ID existence. The Supabase write itself is
      // the source of truth — if a stale UUID slips through, the DB just
      // stores it and the next refresh() reconciles. Cycle protection
      // below remains intact because `ancestorsOf` naturally returns
      // empty for ids not in the local members map (a missing row has no
      // ancestors yet, so it cannot create a cycle).
      const cleanIds = Array.from(new Set(parentIds))
        .filter((pid) => !!pid && pid !== memberId);

      // ── Spouse-as-parent guard ──
      // Universal-engine rule: spouse_id and parent_ids must never
      // overlap on the SAME row. The id of the member's own spouse can
      // never become one of their own parents. Separated co-parents are
      // allowed (a child's parent_ids is independent of those parents'
      // spouse_id values) — this guard fires only when memberId itself
      // is both spouse-of and parent-of-the-same-other-id, which is
      // never structurally meaningful and would corrupt the engine's
      // ancestor walks. We strip silently rather than throw so an
      // accidental UI auto-fill never blocks the save.
      const target = members.find((m) => m.id === memberId);
      const spouseOfTarget = target?.spouseId ?? null;
      const filteredForSpouse = spouseOfTarget
        ? cleanIds.filter((pid) => pid !== spouseOfTarget)
        : cleanIds;

      // Cycle guard: if any candidate parent has memberId in its ancestor
      // chain, skip it (would create a loop). Unknown ids (not yet in the
      // local cache) are safe — they have no ancestors locally, so the
      // walk returns empty.
      const ancestorsOf = (id: string): Set<string> => {
        const seen = new Set<string>();
        const stack = [id];
        while (stack.length) {
          const cur = stack.pop()!;
          const m = members.find((x) => x.id === cur);
          if (!m) continue;
          for (const pid of m.parentIds ?? []) {
            if (!seen.has(pid)) {
              seen.add(pid);
              stack.push(pid);
            }
          }
        }
        return seen;
      };
      const safe = filteredForSpouse.filter((pid) => !ancestorsOf(pid).has(memberId));

      // ---- ORPHAN-PREVENTION DIAGNOSTIC (non-blocking) ----
      // If this update would CLEAR every parent link from a member who
      // has children of their own, surface a console warning so the
      // operator can audit the change. We never block the write here —
      // some users legitimately want to detach a branch — but we log it
      // so any "the tree suddenly fell apart" report becomes traceable.
      // UI surfaces can call `evaluateParentRemoval` from
      // `@/lib/treeSafeguards` directly to gate the action with a
      // confirm dialog when needed.
      if (target) {
        const previousCount = (target.parentIds ?? []).filter(Boolean).length;
        const nextCount = safe.length;
        if (previousCount > 0 && nextCount === 0) {
          const hasDescendants = members.some((m) =>
            (m.parentIds ?? []).includes(memberId),
          );
          if (hasDescendants) {
            // eslint-disable-next-line no-console
            console.warn(
              '[FamilyGraph] linkParents — clearing all parents on a member with descendants',
              {
                memberId,
                memberName: target.name,
                previousParentIds: target.parentIds,
              },
            );
          }
        }
      }
      const result =
        await gatewaySetParents(
          {
            supabase,
            actingUserId:
              user?.id ?? null,
            members,
            viewerMemberId,
            source:
              'add-graph-member',
          },
          memberId,
          safe,
        );

      if (!result.ok) {
        throw new Error(
          result.reason ||
            'The Tree mutation gateway blocked this parent update.',
        );
      }

      setMembers((previous) =>
        previous.map((member) =>
          member.id === memberId
            ? {
                ...member,
                parentIds:
                  result.data.parentIds,
              }
            : member,
        ),
      );

      invalidateExternalEvidence(
        'MEMBERS-CHANGED',
      );
    },
    [members, user?.id]
  );





  const linkSpouses = useCallback<
    FamilyGraphContextType['linkSpouses']
  >(
    async (aId, bId) => {
      try {
        const result =
          await gatewaySetSpouse(
            {
              supabase,
              actingUserId:
                user?.id ?? null,
              members,
              viewerMemberId,
              source:
                'add-graph-member',
            },
            aId,
            bId,
          );

        if (!result.ok) {
          console.warn(
            '[FamilyGraph] linkSpouses gateway blocked',
            {
              aId,
              bId,
              blocker:
                result.blocker,
              reason:
                result.reason,
            },
          );

          return;
        }

        setMembers((previous) =>
          previous.map((member) => {
            if (member.id === aId) {
              return {
                ...member,
                spouseId: bId,
              };
            }

            if (
              bId &&
              member.id === bId
            ) {
              return {
                ...member,
                spouseId: aId,
              };
            }

            if (
              !bId &&
              member.spouseId === aId
            ) {
              return {
                ...member,
                spouseId: null,
              };
            }

            return member;
          }),
        );

        invalidateExternalEvidence(
          'SPOUSE-WRITE',
        );

        if (!result.data.verified) {
          console.warn(
            '[FamilyGraph] linkSpouses gateway write completed but verification is pending',
            {
              aId,
              bId,
            },
          );
        }
      } catch (error) {
        // Spouse linking remains non-fatal to the primary
        // parent-child creation workflow.
        console.warn(
          '[FamilyGraph] linkSpouses non-fatal gateway failure',
          error,
        );
      }
    },
    [
      members,
      user?.id,
    ],
  );


  // ─── repairSpouseEdge — explicit two-id force-link helper ─────────────
  // Used by the manual repair flow ("find Terrance Lewis Sr. + find
  // Chekesha Graham-Lewis → create/repair spouse edge between them").
  // Bypasses any closure-stale state and writes the bidirectional
  // spouse_id directly, then verifies + refreshes.
  //
  // Returns:
  //   { success: true,  verified: true }  when both rows agree.
  //   { success: true,  verified: false } when DB writes succeeded but
  //                                       verification still didn't see
  //                                       the link (rare — RLS / network).
  //   { success: false, error: string }   when DB writes errored or one
  //                                       of the rows can't be found.
  //
  // Pure additive helper — never mutates parent_ids, never deletes the
  // person row, never modifies any other field. spouse_id only.
  const repairSpouseEdge = useCallback(
    async (
      memberAId: string,
      memberBId: string,
    ): Promise<{
      success: boolean;
      verified: boolean;
      error?: string;
    }> => {
      const result =
        await gatewaySetSpouse(
          {
            supabase,
            actingUserId:
              user?.id ?? null,
            members,
            viewerMemberId,
            source:
              'add-graph-member',
          },
          memberAId,
          memberBId,
        );

      if (!result.ok) {
        return {
          success: false,
          verified: false,
          error:
            result.reason ||
            'The Tree mutation gateway blocked the spouse repair.',
        };
      }

      setMembers((previous) =>
        previous.map((member) => {
          if (member.id === memberAId) {
            return {
              ...member,
              spouseId: memberBId,
            };
          }

          if (member.id === memberBId) {
            return {
              ...member,
              spouseId: memberAId,
            };
          }

          return member;
        }),
      );

      invalidateExternalEvidence(
        'SPOUSE-WRITE',
      );

      try {
        await refresh();
      } catch {
        // Refresh failure does not undo a verified gateway write.
      }

      return {
        success: true,
        verified:
          result.data.verified,
      };
    },
    [
      members,
      refresh,
      user?.id,
    ],
  );




  // ---------- Identity / claim system ----------

  const searchClaimCandidates = useCallback<FamilyGraphContextType['searchClaimCandidates']>(
    async ({ name, birthYear, birthDate, excludeOwnerUserId }) => {
      const cleanName = (name ?? '').trim();
      if (cleanName.length < 2) return [];
      const tokens = cleanName.toLowerCase().split(/\s+/).filter(Boolean);
      const firstName = tokens[0] ?? '';
      // Search broadly: name ILIKE on first token, only unclaimed placeholders.
      let q = supabase
        .from('family_graph_members')
        .select('*')
        .is('claimed_by_user_id', null)
        .is('deleted_at', null)
        .neq('approval_status', 'rejected')
        .ilike('name', `%${firstName}%`)
        .limit(50);
      if (excludeOwnerUserId) q = q.neq('owner_user_id', excludeOwnerUserId);
      const { data, error } = await q;
      if (error) {
        console.warn('[FamilyGraph] searchClaimCandidates failed', error);
        return [];
      }
      const rows = (data ?? []).map(rowToMember);
      // Score each candidate; keep only matches the heuristic accepts.
      const targetYear = birthDate ? new Date(birthDate).getUTCFullYear() : birthYear ?? null;
      const scored: ClaimCandidate[] = [];
      for (const m of rows) {
        if (!nameSimilar(m.name, cleanName)) continue;
        let score = 0.4;
        const reasons: string[] = ['name match'];
        if (norm(m.name) === norm(cleanName)) {
          score += 0.25;
          reasons[0] = 'exact name match';
        }
        if (targetYear && m.birthYear) {
          const diff = Math.abs(targetYear - m.birthYear);
          if (diff <= 1) {
            score += 0.3;
            reasons.push(`birth year ${m.birthYear}`);
          } else if (diff <= 3) {
            score += 0.15;
            reasons.push(`birth year ~${m.birthYear}`);
          } else {
            // Birth-year mismatch is a strong negative — likely a different person.
            continue;
          }
        }
        if (m.birthDate && birthDate) {
          if (m.birthDate === birthDate) {
            score += 0.2;
            reasons.push('exact date of birth');
          }
        }
        // Lookup owner display name (best effort, single roundtrip per owner).
        scored.push({
          member: m,
          ownerLabel: null,
          reasonLabel: reasons.join(' · '),
          score: Math.min(score, 1),
        });
      }
      scored.sort((a, b) => b.score - a.score);

      // Hydrate owner display labels in one shot.
      const ownerIds = Array.from(new Set(scored.map((c) => c.member.ownerUserId)));
      if (ownerIds.length > 0) {
        const { data: profs } = await supabase
          .from('profiles')
          .select('id,name,family_name')
          .in('id', ownerIds);
        const labelById = new Map<string, string>();
        for (const p of profs ?? []) {
          const lbl =
            (p.name as string | null) ||
            (p.family_name ? `${p.family_name} family` : null) ||
            'A family member';
          labelById.set(p.id as string, lbl);
        }
        for (const c of scored) {
          c.ownerLabel = labelById.get(c.member.ownerUserId) ?? 'A family member';
        }
      }
      return scored.slice(0, 8);
    },
    []
  );

  const claimProfile = useCallback<
    FamilyGraphContextType['claimProfile']
  >(
    async (memberId) => {
      if (!user?.id) {
        throw new Error('Sign in required to claim your profile.');
      }

      const target = members.find(member => member.id === memberId) ?? null;
      if (!target) {
        throw new Error('Profile not found.');
      }

      if (target.claimedByUserId === user.id) {
        setViewerMemberId(memberId);
        return;
      }

      if (
        target.ownerUserId !== user.id ||
        target.side !== 'self' ||
        target.relationship !== 'unknown'
      ) {
        throw new Error(
          'Profile identity binding requires a governed invite or owner-authorized connection.',
        );
      }

      const { data, error } = await supabase.rpc(
        'claim_own_tree_self_profile',
        { p_member_id: memberId },
      );

      if (error) {
        throw new Error(
          error.message || 'The governed self-profile claim was refused.',
        );
      }

      const payload = data as Record<string, unknown> | null;
      if (
        !payload ||
        payload.member_id !== memberId ||
        payload.claimed_by_user_id !== user.id ||
        !['claimed', 'already-claimed'].includes(String(payload.status ?? ''))
      ) {
        throw new Error('Governed self-profile claim returned an invalid certificate.');
      }

      const claimedAt =
        typeof payload.claimed_at === 'string'
          ? payload.claimed_at
          : new Date().toISOString();

      setMembers(previous =>
        previous.map(member =>
          member.id === memberId
            ? {
                ...member,
                profileState: 'claimed',
                claimedByUserId: user.id,
                claimedAt,
              }
            : member,
        ),
      );

      invalidateExternalEvidence('MEMBERS-CHANGED');
      setViewerMemberId(memberId);
    },
    [members, user?.id],
  );


  const unclaimProfile = useCallback<
    FamilyGraphContextType['unclaimProfile']
  >(
    async () => {
      throw new Error(
        'Claim release requires separately governed server lifecycle authority.',
      );
    },
    [],
  );


  const requestMerge = useCallback<FamilyGraphContextType['requestMerge']>(
    async (sourceMemberId, targetMemberId, reason) => {
      if (!user?.id) throw new Error('Sign in required.');
      if (sourceMemberId === targetMemberId) {
        throw new Error('Cannot merge a profile with itself.');
      }
      // Resolve target's owner so the family-graph admin sees the request.
      const { data: target, error: tErr } = await supabase
        .from('family_graph_members')
        .select('owner_user_id')
        .eq('id', targetMemberId)
        .is('deleted_at', null)
        .maybeSingle();
      if (tErr) throw tErr;
      if (!target) throw new Error('Target profile not found.');
      const { data, error } = await supabase
        .from('family_graph_merge_requests')
        .insert({
          owner_user_id: target.owner_user_id,
          source_member_id: sourceMemberId,
          target_member_id: targetMemberId,
          requested_by: user.id,
          reason: reason ?? null,
          status: 'pending',
        })
        .select('*')
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      return {
        id: data.id,
        ownerUserId: data.owner_user_id,
        sourceMemberId: data.source_member_id,
        targetMemberId: data.target_member_id,
        requestedBy: data.requested_by,
        reason: data.reason ?? null,
        status: data.status,
        createdAt: data.created_at,
        resolvedAt: data.resolved_at ?? null,
        resolvedBy: data.resolved_by ?? null,
      };
    },
    [user?.id]
  );

  // ─── Identity binding API (Phase: logged-in user identity) ────────────
  // Compatibility binding API. `claimProfile()` is now restricted to the
  // authenticated account owner's exact self anchor; invite/placecard identity
  // must use governed invite or owner-authorized server authority. It clears
  // so the modal closes once the binding lands. Pure additive helper —
  // never mutates parent_ids / spouse_id / photos / memories. Just routes
  // the user's confirmed identity into the existing claimProfile pipeline.
  const bindViewerToMember = useCallback(
    async (memberId: string): Promise<void> => {
      await claimProfile(memberId);
      setViewerCandidates([]);
    },
    [claimProfile],
  );

  const value = useMemo<FamilyGraphContextType>(
    () => ({
      members,
      memories,
      photos,
      comments,
      loading,
      effectiveOwnerUserId,
      viewerMemberId,
      viewerCandidates,
      lastRefreshError,
      lastTargetOwner,
      lastFetchedCount,
      bindViewerToMember,
      addMember,
      updateMember,
      approveMember,
      rejectMember,
      deleteMember,
      rollbackCreatedMember,
      restoreMember,
      addMemory,
      updateMemory,
      deleteMemory,
      confirmMember,
      addPhoto,
      updatePhoto,
      deletePhoto,
      addComment,
      deleteComment,
      findPossibleDuplicates,
      connectExistingAncestryAtomic,
      linkParents,
      linkSpouses,
      repairSpouseEdge,
      searchClaimCandidates,
      claimProfile,
      unclaimProfile,
      requestMerge,
      isBulkWriting,
      runBulkAdd,
      scheduleRefresh,
      refresh,
    }),
    [
      members,
      memories,
      photos,
      comments,
      loading,
      effectiveOwnerUserId,
      viewerMemberId,
      viewerCandidates,
      lastRefreshError,
      lastTargetOwner,
      lastFetchedCount,
      bindViewerToMember,
      addMember,
      updateMember,
      approveMember,
      rejectMember,
      deleteMember,
      rollbackCreatedMember,
      restoreMember,
      addMemory,
      updateMemory,
      deleteMemory,
      confirmMember,
      addPhoto,
      updatePhoto,
      deletePhoto,
      addComment,
      deleteComment,
      findPossibleDuplicates,
      linkParents,
      linkSpouses,
      repairSpouseEdge,
      searchClaimCandidates,
      claimProfile,
      unclaimProfile,
      requestMerge,
      isBulkWriting,
      runBulkAdd,
      scheduleRefresh,
      refresh,
    ]
  );




  return <FamilyGraphContext.Provider value={value}>{children}</FamilyGraphContext.Provider>;
};



export const useFamilyGraph = () => {
  const ctx = useContext(FamilyGraphContext);
  if (!ctx) throw new Error('useFamilyGraph must be used within FamilyGraphProvider');
  return ctx;
};


// ---------- Smart Suggestion engine ----------
export interface RelativeSuggestion {
  id: string;
  label: string;
  relationship: Relationship;
  side: Side;
  trigger: GraphMember;
}

/**
 * Build a list of "missing relative" suggestions for the current family graph.
 *
 * IMPORTANT — additive filtering only:
 *  - We never invent or store new relationships here.
 *  - We never duplicate suggestions for relatives that already exist in the
 *    tree. If a sibling of "you" is already added we do NOT suggest siblings
 *    again; if any grandparent on Mom's side already exists we do NOT
 *    suggest Mom's parents again, even if her parent_ids weren't linked.
 *  - All checks read existing rows from `members` only. No schema changes.
 *
 * Internal debug: when `window.__KINS_DEBUG__` is truthy we log which
 * suggestions were filtered out and why, so we can diagnose duplicate
 * recommendations from the browser console without exposing any UI.
 */
export const buildSuggestions = (members: GraphMember[]): RelativeSuggestion[] => {
  const out: RelativeSuggestion[] = [];

  // ---------- helpers (read-only over existing members) ----------
  const active = (m: GraphMember) => m.approvalStatus !== 'rejected';
  const live = members.filter(active);

  /** Does any active member match this stored relationship (and optionally side)? */
  const hasRel = (rel: Relationship, side?: Side) =>
    live.some(
      (m) =>
        m.relationship === rel &&
        (side ? m.side === side || m.side === 'both' : true)
    );

  /** Is `pid` already listed as a parent on any active member? */
  const someoneHasParent = (pid: string) =>
    live.some((m) => (m.parentIds ?? []).includes(pid));

  /**
   * Does the trigger already have at least one recorded ancestor at the
   * NEXT generation up? Uses linked parent_ids first, then falls back to
   * a side-based existence check so we don't keep nagging users who
   * already added the relative without linking parent_ids.
   *
   *   - mother / father → suggest grandparents only if no grandparent on
   *     this side exists at all (and no parent_ids link).
   *   - grandparent    → suggest great-grandparents only if no
   *     great-grandparent on this side exists.
   *   - cousin         → suggest their parents (your aunt/uncle) only if
   *     no aunt/uncle on this side exists.
   */
  const triggerHasLinkedParents = (t: GraphMember): boolean => {
    // Linked parent_ids — strongest evidence.
    const linkedFromIds = (t.parentIds ?? []).some((pid) =>
      live.some((m) => m.id === pid)
    );
    if (linkedFromIds) return true;

    const sideKey: Side =
      t.side === 'self' || t.side === 'both' ? 'both' : t.side;
    const sameSide = (m: GraphMember) =>
      m.side === sideKey || m.side === 'both' || sideKey === 'both';

    if (t.relationship === 'mother' || t.relationship === 'father') {
      return live.some(
        (m) => m.relationship === 'grandparent' && sameSide(m)
      );
    }
    if (t.relationship === 'grandparent') {
      return live.some(
        (m) => m.relationship === 'great-grandparent' && sameSide(m)
      );
    }
    if (t.relationship === 'cousin') {
      return live.some(
        (m) => m.relationship === 'aunt-uncle' && sameSide(m)
      );
    }
    return false;
  };

  /**
   * Does the trigger already have a recorded sibling? A sibling here means
   * another active member who either:
   *   (a) shares at least one parent_id with the trigger, OR
   *   (b) — when parent_ids are not yet filled in — sits in the same
   *       "aunt/uncle bucket" on the same side (your mom's siblings live
   *       on mom's side, etc.). We use the side as a soft fallback so we
   *       don't repeatedly nag the user to add aunts/uncles they've
   *       already created with `relationship='aunt-uncle'`.
   */
  const triggerHasSibling = (t: GraphMember) => {
    const tParents = new Set((t.parentIds ?? []).filter(Boolean));
    if (tParents.size > 0) {
      const linked = live.some(
        (m) =>
          m.id !== t.id &&
          (m.parentIds ?? []).some((pid) => tParents.has(pid))
      );
      if (linked) return true;
    }
    // Fallback: if trigger is a parent (mother/father), any aunt-uncle on
    // the same side counts as "their sibling already added".
    if (t.relationship === 'mother' || t.relationship === 'father') {
      const sideKey: Side = t.side === 'self' || t.side === 'both' ? 'both' : t.side;
      return live.some(
        (m) =>
          m.relationship === 'aunt-uncle' &&
          (m.side === sideKey || m.side === 'both' || sideKey === 'both')
      );
    }
    // For a grandparent trigger, "their other children" === aunts/uncles on
    // that side. Re-use the same side check.
    if (t.relationship === 'grandparent') {
      const sideKey: Side = t.side === 'self' || t.side === 'both' ? 'both' : t.side;
      return live.some(
        (m) =>
          m.relationship === 'aunt-uncle' &&
          (m.side === sideKey || m.side === 'both' || sideKey === 'both')
      );
    }
    return false;
  };

  /** Does the trigger already have at least one recorded child? */
  const triggerHasChild = (t: GraphMember) => {
    if (someoneHasParent(t.id)) return true;
    // Soft fallback for cousin → cousin-child on the same side.
    if (t.relationship === 'cousin') {
      const sideKey: Side = t.side === 'self' || t.side === 'both' ? 'both' : t.side;
      return live.some(
        (m) =>
          m.relationship === 'cousin-child' &&
          (m.side === sideKey || m.side === 'both' || sideKey === 'both')
      );
    }
    // Soft fallback for sibling → niece/nephew (stored as cousin-child).
    if (t.relationship === 'sibling') {
      return live.some((m) => m.relationship === 'cousin-child');
    }
    // Soft fallback for aunt-uncle → cousin on the same side.
    if (t.relationship === 'aunt-uncle') {
      const sideKey: Side = t.side === 'self' || t.side === 'both' ? 'both' : t.side;
      return live.some(
        (m) =>
          m.relationship === 'cousin' &&
          (m.side === sideKey || m.side === 'both' || sideKey === 'both')
      );
    }
    return false;
  };

  /**
   * Does the trigger already have a spouse (linked OR an adjacent same-
   * generation member that's plausibly their partner)? We treat any other
   * active aunt-uncle on the same side as a likely spouse so we don't keep
   * suggesting "Add Aunt Karen's spouse" once you've added Uncle Bob too.
   */
  const triggerHasSpouse = (t: GraphMember) => {
    if (t.spouseId) return true;
    if (t.relationship === 'aunt-uncle') {
      const sideKey: Side =
        t.side === 'self' || t.side === 'both' ? 'both' : t.side;
      return (
        live.filter(
          (m) =>
            m.relationship === 'aunt-uncle' &&
            m.id !== t.id &&
            (m.side === sideKey || m.side === 'both' || sideKey === 'both')
        ).length > 0
      );
    }
    return false;
  };

  // Track which suggestions get filtered out and why, for debug logging.
  const filteredOut: Array<{ reason: string; trigger: string; rel: Relationship }> = [];
  const skip = (reason: string, t: GraphMember, rel: Relationship) => {
    filteredOut.push({ reason, trigger: t.name, rel });
  };

  // ---------- per-trigger suggestions ----------
  for (const m of live) {
    const sideForKin: Side =
      m.side === 'self' || m.side === 'both' ? 'both' : m.side;

    if (m.relationship === 'mother' || m.relationship === 'father') {
      if (!triggerHasLinkedParents(m)) {
        out.push({
          id: `${m.id}-gp`,
          label: `Add ${m.name}'s parents`,
          relationship: 'grandparent',
          side: sideForKin,
          trigger: m,
        });
      } else {
        skip('grandparent already exists on side', m, 'grandparent');
      }
      if (!triggerHasSibling(m)) {
        out.push({
          id: `${m.id}-au`,
          label: `Add ${m.name}'s siblings (your aunts/uncles)`,
          relationship: 'aunt-uncle',
          side: sideForKin,
          trigger: m,
        });
      } else {
        skip('aunt/uncle already exists on side', m, 'aunt-uncle');
      }
    }

    if (m.relationship === 'grandparent') {
      if (!triggerHasLinkedParents(m)) {
        out.push({
          id: `${m.id}-ggp`,
          label: `Add ${m.name}'s parents (great-grandparents)`,
          relationship: 'great-grandparent',
          side: sideForKin,
          trigger: m,
        });
      } else {
        skip('great-grandparent already exists', m, 'great-grandparent');
      }
      if (!triggerHasSibling(m)) {
        out.push({
          id: `${m.id}-gpc`,
          label: `Add ${m.name}'s other children`,
          relationship: 'aunt-uncle',
          side: sideForKin,
          trigger: m,
        });
      } else {
        skip('aunt/uncle already exists on side', m, 'aunt-uncle');
      }
    }

    if (m.relationship === 'aunt-uncle') {
      if (!triggerHasSpouse(m)) {
        out.push({
          id: `${m.id}-aus`,
          label: `Add ${m.name}'s spouse`,
          relationship: 'aunt-uncle',
          side: sideForKin,
          trigger: m,
        });
      } else {
        skip('aunt/uncle spouse already linked or present', m, 'aunt-uncle');
      }
      if (!triggerHasChild(m)) {
        out.push({
          id: `${m.id}-cou`,
          label: `Add ${m.name}'s children (your cousins)`,
          relationship: 'cousin',
          side: sideForKin,
          trigger: m,
        });
      } else {
        skip('cousin already exists on side', m, 'cousin');
      }
    }

    if (m.relationship === 'cousin') {
      if (!triggerHasLinkedParents(m)) {
        out.push({
          id: `${m.id}-cp`,
          label: `Add ${m.name}'s parents`,
          relationship: 'aunt-uncle',
          side: sideForKin,
          trigger: m,
        });
      } else {
        skip('aunt/uncle already exists on side', m, 'aunt-uncle');
      }
      if (!triggerHasSpouse(m)) {
        out.push({
          id: `${m.id}-cs`,
          label: `Add ${m.name}'s spouse`,
          relationship: 'cousin',
          side: sideForKin,
          trigger: m,
        });
      } else {
        skip('cousin spouse already linked', m, 'cousin');
      }
      if (!triggerHasChild(m)) {
        out.push({
          id: `${m.id}-cc`,
          label: `Add ${m.name}'s children`,
          relationship: 'cousin-child',
          side: sideForKin,
          trigger: m,
        });
      } else {
        skip('cousin-child already exists on side', m, 'cousin-child');
      }
    }

    if (m.relationship === 'sibling') {
      if (!triggerHasChild(m)) {
        out.push({
          id: `${m.id}-sc`,
          label: `Add ${m.name}'s children (your nieces/nephews)`,
          relationship: 'cousin-child',
          side: sideForKin,
          trigger: m,
        });
      } else {
        skip('niece/nephew already exists', m, 'cousin-child');
      }
    }
  }

  // ---------- top-level "missing core relatives" suggestions ----------
  // Only show if the user truly has no record of that relationship at all.
  // We treat step-parent / adopted-parent on the same side as already
  // covering "Add your mom/dad" so we don't pester users who chose those.
  const hasMomEquivalent =
    hasRel('mother') ||
    live.some(
      (m) =>
        (m.relationship === 'step-parent' || m.relationship === 'adopted-parent') &&
        (m.side === 'mother' || m.side === 'both')
    );
  const hasDadEquivalent =
    hasRel('father') ||
    live.some(
      (m) =>
        (m.relationship === 'step-parent' || m.relationship === 'adopted-parent') &&
        (m.side === 'father' || m.side === 'both')
    );
  if (!hasMomEquivalent) {
    out.unshift({
      id: 'top-mom',
      label: 'Add your mom',
      relationship: 'mother',
      side: 'mother',
      trigger: live[0] ?? ({} as GraphMember),
    });
  }
  if (!hasDadEquivalent) {
    out.unshift({
      id: 'top-dad',
      label: 'Add your dad',
      relationship: 'father',
      side: 'father',
      trigger: live[0] ?? ({} as GraphMember),
    });
  }

  // ---------- de-dupe (label-level + relationship+side+trigger-level) ----------
  const seenLabel = new Set<string>();
  const seenKey = new Set<string>();
  const finalList = out.filter((s) => {
    if (seenLabel.has(s.label)) return false;
    const key = `${s.relationship}|${s.side}|${s.trigger?.id ?? ''}`;
    if (seenKey.has(key)) return false;
    seenLabel.add(s.label);
    seenKey.add(key);
    return true;
  });

  // Internal debug — silently logs to the console when explicitly enabled
  // via `window.__KINS_DEBUG__ = true`. Never shows UI.
  if (typeof window !== 'undefined' && (window as any).__KINS_DEBUG__) {
    // eslint-disable-next-line no-console
    console.debug('[KinsCircle] buildSuggestions', {
      totalMembers: members.length,
      activeMembers: live.length,
      suggestionsKept: finalList.length,
      filteredOut,
    });
  }

  return finalList;
};




// ---------- Life timeline helpers ----------

export const LIFE_STAGES = [
  { id: 'birth', label: 'Birth & early life', minAge: 0, maxAge: 5 },
  { id: 'childhood', label: 'Childhood', minAge: 6, maxAge: 12 },
  { id: 'school', label: 'School years', minAge: 13, maxAge: 21 },
  { id: 'marriage', label: 'Marriage & family', minAge: 22, maxAge: 39 },
  { id: 'work', label: 'Work & community', minAge: 40, maxAge: 59 },
  { id: 'later', label: 'Later years', minAge: 60, maxAge: 200 },
  { id: 'legacy', label: 'Legacy memories', minAge: -1, maxAge: -1 }, // posthumous / dedicated
  { id: 'undated', label: 'Undated memories', minAge: -2, maxAge: -2 },
] as const;
export type LifeStageId = (typeof LIFE_STAGES)[number]['id'];

/**
 * Resolve the life-stage of a single timeline entry given the subject's
 * date of birth (if known). Posthumous events (after deceased) → legacy.
 * No event date or birth date → undated. Manual `lifeStage` overrides.
 */
export const resolveLifeStage = (
  member: GraphMember,
  eventDate?: string | null,
  manualStage?: string | null
): LifeStageId => {
  if (manualStage) {
    const found = LIFE_STAGES.find((s) => s.id === manualStage);
    if (found) return found.id as LifeStageId;
  }
  if (!eventDate) return 'undated';
  const evt = new Date(eventDate);
  if (Number.isNaN(evt.getTime())) return 'undated';

  // Compute birth date for age math
  let birth: Date | null = null;
  if (member.birthDate) {
    const d = new Date(member.birthDate);
    if (!Number.isNaN(d.getTime())) birth = d;
  }
  if (!birth && member.birthYear) {
    birth = new Date(Date.UTC(member.birthYear, (member.birthMonth ?? 1) - 1, member.birthDay ?? 1));
  }
  if (!birth) return 'undated';

  // Posthumous → legacy
  if (member.livingStatus === 'deceased') {
    // Heuristic: if the event is after their last recorded year (or today for living),
    // treat as legacy. Without exact death date we approximate with "today minus 0".
    if (evt.getTime() > Date.now()) return 'legacy';
  }

  const ageMs = evt.getTime() - birth.getTime();
  if (ageMs < 0) return 'birth';
  const ageYears = ageMs / (365.25 * 24 * 60 * 60 * 1000);
  for (const s of LIFE_STAGES) {
    if (s.minAge >= 0 && ageYears >= s.minAge && ageYears <= s.maxAge) {
      return s.id as LifeStageId;
    }
  }
  return 'later';
};

// ---------- Relationship inference engine ----------

/**
 * Walk the parent_ids graph from "you" (implicit anchor) outward to determine
 * what each member's relationship label *should* be. The user is the implicit
 * root. Members that are direct parents (mother/father/step-parent/adopted-parent)
 * of "you" form the seed of the ancestor traversal.
 *
 * Rules implemented:
 *  - parent of a parent → grandparent
 *  - parent of a grandparent → great-grandparent
 *  - sibling of a parent → aunt-uncle (shares parents with a direct parent)
 *  - child of an aunt/uncle → cousin
 *  - child of a sibling → cousin-child (niece/nephew bucket)
 *  - members that share all parents with "you"'s parents → sibling
 */
export interface InferenceResult {
  /** Map<memberId, inferred relationship label>. */
  byId: Map<string, Relationship>;
  /** Members that have at least one ancestor or descendant link to "you". */
  connectedIds: Set<string>;
}

export const inferRelationships = (members: GraphMember[]): InferenceResult => {
  const byId = new Map<string, Relationship>();
  const connectedIds = new Set<string>();
  const memberMap = new Map(members.map((m) => [m.id, m]));

  // Identify direct parents of "you".
  const directParents = members.filter(
    (m) =>
      m.relationship === 'mother' ||
      m.relationship === 'father' ||
      m.relationship === 'step-parent' ||
      m.relationship === 'adopted-parent'
  );

  // BFS up the parent chain from each direct parent.
  // depth 1 = parent (already labeled), 2 = grandparent, 3 = great-grandparent.
  const ancestorDepth = new Map<string, number>();
  for (const p of directParents) {
    ancestorDepth.set(p.id, 1);
  }
  const queue: string[] = directParents.map((p) => p.id);
  while (queue.length) {
    const id = queue.shift()!;
    const m = memberMap.get(id);
    if (!m) continue;
    const depth = ancestorDepth.get(id)!;
    for (const pid of m.parentIds ?? []) {
      if (!memberMap.has(pid)) continue;
      const prev = ancestorDepth.get(pid);
      const next = depth + 1;
      if (prev === undefined || next < prev) {
        ancestorDepth.set(pid, next);
        queue.push(pid);
      }
    }
  }
  for (const [id, depth] of ancestorDepth) {
    connectedIds.add(id);
    if (depth === 1) {
      // keep stored mother/father/step-parent/adopted-parent label
      const m = memberMap.get(id)!;
      byId.set(id, m.relationship);
    } else if (depth === 2) byId.set(id, 'grandparent');
    else if (depth >= 3) byId.set(id, 'great-grandparent');
  }

  // Find aunts / uncles: members that share a parent with one of "you"'s
  // direct parents, but are NOT a direct parent themselves.
  for (const m of members) {
    if (ancestorDepth.has(m.id)) continue;
    const myParents = new Set(m.parentIds ?? []);
    if (myParents.size === 0) continue;
    for (const dp of directParents) {
      const dpParents = new Set(dp.parentIds ?? []);
      if (dpParents.size === 0) continue;
      // share at least one parent
      let shares = false;
      for (const pid of myParents) {
        if (dpParents.has(pid)) {
          shares = true;
          break;
        }
      }
      if (shares) {
        byId.set(m.id, 'aunt-uncle');
        connectedIds.add(m.id);
        break;
      }
    }
  }

  // Cousins: child of an aunt/uncle (their parent is an aunt/uncle node).
  for (const m of members) {
    if (byId.has(m.id)) continue;
    const myParents = m.parentIds ?? [];
    if (myParents.some((pid) => byId.get(pid) === 'aunt-uncle')) {
      byId.set(m.id, 'cousin');
      connectedIds.add(m.id);
    }
  }
  // Cousin's children: their parent is a cousin.
  for (const m of members) {
    if (byId.has(m.id)) continue;
    const myParents = m.parentIds ?? [];
    if (myParents.some((pid) => byId.get(pid) === 'cousin')) {
      byId.set(m.id, 'cousin-child');
      connectedIds.add(m.id);
    }
  }

  // Mark members whose stored relationship is 'self', 'sibling', 'spouse',
  // 'child' as connected too (they are direct relations of "you").
  for (const m of members) {
    if (
      m.side === 'self' ||
      m.relationship === 'sibling' ||
      m.relationship === 'spouse' ||
      m.relationship === 'child'
    ) {
      connectedIds.add(m.id);
      if (!byId.has(m.id)) byId.set(m.id, m.relationship);
    }
  }

  return { byId, connectedIds };
};

/**
 * For a given member, return the best display label given the inference
 * results. Falls back to the stored `relationship` if no inference exists.
 */
export const displayedRelationship = (
  member: GraphMember,
  inference: InferenceResult
): Relationship => inference.byId.get(member.id) ?? member.relationship;


// ---------- Viewer-centered relationship projection ----------

/**
 * Re-label every member in a shared family graph from the perspective of
 * a specific VIEWER member (the row that represents the currently logged-
 * in user). This is what enables a single shared family tree to look
 * different to each logged-in user — your own claimed profile becomes
 * "YOU", and every other relative is renamed to their relationship to YOU.
 *
 * IMPORTANT — pure projection, never mutates DB:
 *   - The returned array contains shallow-cloned members with their
 *     `relationship` and `side` fields rewritten relative to the viewer.
 *   - Stored rows on the database are untouched. This is purely a
 *     presentation projection so the shared graph can be re-centered
 *     for whoever is currently viewing it.
 *   - Members that have NO derivable relationship to the viewer keep
 *     their stored relationship + side untouched (so they don't fall off
 *     the tree / roster). Callers can detect "needs connection" via the
 *     normal connectedIds check.
 *
 * Algorithm:
 *   1. Build adjacency: for every member, parents (via parent_ids) and
 *      children (reverse parent_ids).
 *   2. The viewer is the implicit root.
 *   3. Walk parents of viewer → label as 'mother'/'father' (preserving
 *      the original label when one parent is stored as 'mother' vs
 *      'father'; if not, alternate by index).
 *   4. Walk parents-of-parents → 'grandparent'; one more level →
 *      'great-grandparent'. Side propagates from the parent.
 *   5. Walk children of viewer → 'child'.
 *   6. Children of children → 'cousin-child' (closest existing label
 *      for descendants beyond direct kids).
 *   7. Members sharing at least one parent with the viewer → 'sibling'
 *      (side='self').
 *   8. Members sharing a parent with one of the viewer's parents (but
 *      not the viewer themselves) → 'aunt-uncle'. Their side mirrors
 *      the parent they share with.
 *   9. Children of aunts/uncles → 'cousin'. Side mirrors the aunt/uncle.
 *  10. Children of siblings → 'cousin-child' (niece/nephew bucket). Side
 *      stays 'self' (it's your sibling's branch).
 *  11. Spouse of viewer (via spouse_id) → 'spouse', side='self'. Spouses
 *      of other relatives keep their stored relationship.
 *
 * Anything not matched by the above keeps its stored relationship/side.
 */
export const projectMembersForViewer = (
  members: GraphMember[],
  viewerId: string | null
): GraphMember[] => {
  if (!viewerId) return members;
  const viewer = members.find((m) => m.id === viewerId);
  if (!viewer) return members;

  const byId = new Map(members.map((m) => [m.id, m]));
  // Children index (parent_id → list of children member ids).
  const childrenOf = new Map<string, string[]>();
  for (const m of members) {
    for (const pid of m.parentIds ?? []) {
      if (!childrenOf.has(pid)) childrenOf.set(pid, []);
      childrenOf.get(pid)!.push(m.id);
    }
  }

  // Output map: id → { relationship, side } overrides.
  const overrides = new Map<string, { relationship: Relationship; side: Side }>();

  // 1. Viewer themselves — anchor as side='self', relationship preserved
  //    as a marker the tree filter recognizes (existing tree filters
  //    `side === 'self' && relationship === 'unknown'` to drop the
  //    self-anchor row, replacing it with the YOU card). Force that.
  overrides.set(viewer.id, { relationship: 'unknown', side: 'self' });

  // 2. Spouse of viewer → 'spouse' (RESTORED — OPTIONAL display layer).
  //    The viewer's stored spouse_id partner is labeled 'spouse'/side='self'
  //    so they render beside the viewer with the heart-line connector.
  //    parent_ids walks below override this if the spouse also turns out
  //    to be a structural blood relative (e.g. they share a parent),
  //    which is the safety guarantee that "parent-child logic always
  //    wins". For the common case of an unrelated partner, this label
  //    sticks. Pure projection — never mutates spouse_id.
  if (viewer.spouseId && byId.has(viewer.spouseId)) {
    overrides.set(viewer.spouseId, { relationship: 'spouse', side: 'self' });
  }



  // 3. Direct parents of viewer.
  const viewerParentIds = (viewer.parentIds ?? []).filter((pid) => byId.has(pid));
  // Try to honor stored mother/father labels first.
  const motherParentId = viewerParentIds.find(
    (pid) => byId.get(pid)?.relationship === 'mother'
  );
  const fatherParentId = viewerParentIds.find(
    (pid) => byId.get(pid)?.relationship === 'father'
  );
  // If no stored hint, use the first as mother and second as father —
  // this is a best-effort visual default; the user can re-label later.
  const orderedParents: Array<{ id: string; rel: Relationship; side: Side }> = [];
  for (const pid of viewerParentIds) {
    const stored = byId.get(pid)!;
    let rel: Relationship = stored.relationship;
    let side: Side = stored.side;
    if (pid === motherParentId) {
      rel = 'mother';
      side = 'mother';
    } else if (pid === fatherParentId) {
      rel = 'father';
      side = 'father';
    } else if (
      rel !== 'mother' &&
      rel !== 'father' &&
      rel !== 'step-parent' &&
      rel !== 'adopted-parent'
    ) {
      // Parent-array order is not identity authority.
      // Preserve the stored relationship and side until the user or
      // structural evidence explicitly identifies mother/father.
      rel = stored.relationship;
      side = stored.side;
    }
    orderedParents.push({ id: pid, rel, side });
    overrides.set(pid, { relationship: rel, side });
  }

  // 4. Walk ancestors above the direct parents.
  //    depth 2 = grandparent, depth 3+ = great-grandparent.
  const ancestorDepth = new Map<string, number>();
  const ancestorSide = new Map<string, Side>();
  for (const p of orderedParents) {
    ancestorDepth.set(p.id, 1);
    ancestorSide.set(p.id, p.side);
  }
  const queue: string[] = orderedParents.map((p) => p.id);
  while (queue.length) {
    const id = queue.shift()!;
    const m = byId.get(id);
    if (!m) continue;
    const depth = ancestorDepth.get(id)!;
    const side = ancestorSide.get(id)!;
    for (const pid of m.parentIds ?? []) {
      if (!byId.has(pid)) continue;
      if (ancestorDepth.has(pid)) continue;
      ancestorDepth.set(pid, depth + 1);
      ancestorSide.set(pid, side);
      queue.push(pid);
      const newRel: Relationship =
        depth + 1 === 2 ? 'grandparent' : 'great-grandparent';
      overrides.set(pid, { relationship: newRel, side });
    }
  }

  // 5. Siblings of viewer — share at least one parent_id with viewer.
  if (viewerParentIds.length > 0) {
    const viewerParentSet = new Set(viewerParentIds);
    for (const m of members) {
      if (m.id === viewer.id) continue;
      if (overrides.has(m.id)) continue;
      const shares = (m.parentIds ?? []).some((pid) => viewerParentSet.has(pid));
      if (shares) {
        overrides.set(m.id, { relationship: 'sibling', side: 'self' });
      }
    }
  }

  // 6. Aunts/uncles — siblings of viewer's parents (share a parent with
  //    a viewer-parent, but are NOT the viewer themselves and NOT already
  //    labeled). Side mirrors that parent.
  for (const p of orderedParents) {
    const parentParents = new Set((byId.get(p.id)?.parentIds ?? []).filter((pid) => byId.has(pid)));
    if (parentParents.size === 0) continue;
    for (const m of members) {
      if (m.id === viewer.id) continue;
      if (m.id === p.id) continue;
      if (overrides.has(m.id)) continue;
      const shares = (m.parentIds ?? []).some((pid) => parentParents.has(pid));
      if (shares) {
        overrides.set(m.id, { relationship: 'aunt-uncle', side: p.side });
      }
    }
  }

  // 7. Children of viewer.
  const directChildIds = childrenOf.get(viewer.id) ?? [];
  for (const cid of directChildIds) {
    if (overrides.has(cid)) continue;
    overrides.set(cid, { relationship: 'child', side: 'self' });
  }

  // 8. Cousins — children of aunts/uncles. Side mirrors the aunt/uncle.
  //    Iterate over current overrides snapshot to avoid mutation while
  //    iterating.
  const auntUncleSnapshot = Array.from(overrides.entries()).filter(
    ([, v]) => v.relationship === 'aunt-uncle'
  );
  for (const [auId, auVal] of auntUncleSnapshot) {
    const kids = childrenOf.get(auId) ?? [];
    for (const cid of kids) {
      if (overrides.has(cid)) continue;
      overrides.set(cid, { relationship: 'cousin', side: auVal.side });
    }
  }

  // 9. Cousin's children — children of cousins. Side mirrors cousin.
  const cousinSnapshot = Array.from(overrides.entries()).filter(
    ([, v]) => v.relationship === 'cousin'
  );
  for (const [cid, cVal] of cousinSnapshot) {
    const kids = childrenOf.get(cid) ?? [];
    for (const gcid of kids) {
      if (overrides.has(gcid)) continue;
      overrides.set(gcid, { relationship: 'cousin-child', side: cVal.side });
    }
  }

  // 10. Niblings — children of siblings (stored as cousin-child for tree
  //     placement; the existing renderer puts them under the sibling
  //     branch).
  const siblingSnapshot = Array.from(overrides.entries()).filter(
    ([, v]) => v.relationship === 'sibling'
  );
  for (const [sid] of siblingSnapshot) {
    const kids = childrenOf.get(sid) ?? [];
    for (const nid of kids) {
      if (overrides.has(nid)) continue;
      overrides.set(nid, { relationship: 'cousin-child', side: 'self' });
    }
  }

  // 11. Grandchildren — children of viewer's children. Bucket as
  //     cousin-child so the existing tree puts them on the descendants
  //     row (we don't have a dedicated 'grandchild' relationship type).
  for (const cid of directChildIds) {
    const kids = childrenOf.get(cid) ?? [];
    for (const gcid of kids) {
      if (overrides.has(gcid)) continue;
      overrides.set(gcid, { relationship: 'cousin-child', side: 'self' });
    }
  }

  // Build the final projected list. Members with no override keep their
  // stored relationship/side untouched — they remain in the roster but
  // won't necessarily land in a structured tier on the tree (the existing
  // safety-net overflow row catches them so nothing disappears).
  return members.map((m) => {
    const o = overrides.get(m.id);
    if (!o) return m;
    return { ...m, relationship: o.relationship, side: o.side };
  });
};

// ---------- Generational kinship engine ----------
//
// Pure, additive engine that computes the precise relationship label between
// two members of a family graph at ANY generational distance. Unlike
// `projectMembersForViewer`, which exists to slot members into the FIXED set
// of visual tiers used by the tree renderer (parent / grandparent /
// great-grandparent / aunt-uncle / cousin / cousin-child …), this engine is
// allowed to return labels of unlimited length:
//
//   - "Great-Great-Great-Grandmother"
//   - "Great-Great-Niece"
//   - "Second cousin once removed"
//   - "Extended relative" (with a step-by-step path) for anything the
//     algorithm can't classify confidently.
//
// IMPORTANT design rules:
//   - We only consume the four CORE relationships stored on rows:
//     parent (via parent_ids), child (reverse parent_ids), sibling
//     (shared parent_ids), and spouse (spouse_id). Every extended title is
//     derived from a shared-ancestor walk — there is NO storage of
//     "great-aunt" or "second cousin" in the database.
//   - Pure functions: no side effects, no DB writes.
//   - Safe fallback: if the algorithm can't classify a connection
//     confidently, we return code='extended' with a humanized path so the
//     UI can render "Extended relative — You → Mother → Sister → Son".
//   - Manual override: a per-member `relationshipOverride` (admin-set)
//     wins over any computed label when present. Used only for special
//     cases ("Godmother", "Adopted aunt"), never as the primary system.

/** Discriminator for the kind of kinship relationship the engine returns. */
export type KinshipCode =
  | 'self'
  | 'spouse'
  | 'parent'              // direct parent (mother/father/step/adopted)
  | 'child'               // direct child
  | 'sibling'             // share at least one parent
  | 'ancestor'            // grandparent / great-grandparent / great^N-grandparent
  | 'descendant'          // grandchild / great-grandchild / great^N-grandchild
  | 'pibling'             // aunt / uncle / great-aunt / great^N-aunt-uncle
  | 'nibling'             // niece / nephew / great-niece / great^N-niece-nephew
  | 'cousin'              // first / second / third cousin (with optional removal)
  | 'extended'            // anything else — render via path
  | 'unknown';            // no path between viewer and target

export interface KinshipResult {
  code: KinshipCode;
  /** Display label, e.g. "Great-Great-Grandmother" / "Second cousin once removed". */
  label: string;
  /** How many generations the target is ABOVE the viewer along the path. */
  generationsUp: number;
  /** How many generations the target is BELOW the viewer along the path. */
  generationsDown: number;
  /** For cousins: how many "cousin" steps removed (1 = first, 2 = second…). */
  cousinDegree?: number;
  /** For cousins: generational removal (e.g. "once removed"). */
  removal?: number;
  /**
   * Step-by-step description, e.g.
   *   ["You", "Mother", "Sister", "Son", "Child"]
   * Always starts with "You" and ends with the target's display label.
   */
  path?: string[];
}

/** Build "Great-…-Great-{base}" prefix string for n>=0 extra "great" levels. */
const greatPrefix = (n: number): string => {
  if (n <= 0) return '';
  if (n === 1) return 'Great-';
  // "Great-Great-Great-…" — capitalized like a proper title.
  return Array.from({ length: n }, () => 'Great').join('-') + '-';
};

/** Ordinal helper for cousin degree: 1 → "First", 2 → "Second", … */
const ordinal = (n: number): string => {
  const small = ['', 'First', 'Second', 'Third', 'Fourth', 'Fifth',
    'Sixth', 'Seventh', 'Eighth', 'Ninth', 'Tenth'];
  if (n >= 1 && n < small.length) return small[n];
  // Fallback for very distant cousins.
  const suffix =
    n % 100 >= 11 && n % 100 <= 13 ? 'th' :
    n % 10 === 1 ? 'st' :
    n % 10 === 2 ? 'nd' :
    n % 10 === 3 ? 'rd' : 'th';
  return `${n}${suffix}`;
};

/** Removal phrasing: 0 → "", 1 → "once removed", 2 → "twice removed", n → "n times removed". */
const removalPhrase = (r: number): string => {
  if (r <= 0) return '';
  if (r === 1) return ' once removed';
  if (r === 2) return ' twice removed';
  if (r === 3) return ' thrice removed';
  return ` ${r} times removed`;
};

/**
 * Walk parent_ids upward from `startId` to compute every ancestor and the
 * shortest distance to that ancestor. Returns Map<ancestorId, depth>.
 * depth 0 = startId itself (so callers can detect "is this me").
 *
 * CYCLE-SAFE: terminates after every reachable id is in the map. Uses a
 * hard visit cap as a secondary defensive bound — if a future change ever
 * introduced a malformed parent_ids cycle that exhausted the inequality
 * guard, we still terminate cleanly instead of blowing the call stack.
 */
const KINSHIP_MAX_VISITS = 50_000;
const ancestorDistances = (
  members: GraphMember[],
  startId: string
): Map<string, number> => {
  const map = new Map<string, number>();
  const byId = new Map(members.map((m) => [m.id, m]));
  const queue: Array<[string, number]> = [[startId, 0]];
  map.set(startId, 0);
  let visits = 0;
  while (queue.length) {
    if (visits++ > KINSHIP_MAX_VISITS) {
      // eslint-disable-next-line no-console
      console.warn('[KinsCircle][kinship] ancestorDistances visit cap hit', {
        startId,
        mappedSize: map.size,
      });
      break;
    }
    const [id, depth] = queue.shift()!;
    const m = byId.get(id);
    if (!m) continue;
    for (const pid of m.parentIds ?? []) {
      if (!byId.has(pid)) continue;
      const prev = map.get(pid);
      const next = depth + 1;
      if (prev === undefined || next < prev) {
        map.set(pid, next);
        queue.push([pid, next]);
      }
    }
  }
  return map;
};


/**
 * Compute the kinship between `viewerId` and `targetId` based on the shared
 * family graph.
 *
 * Algorithm:
 *  1. If they're the same row → 'self'.
 *  2. If linked via spouseId on either side → 'spouse'.
 *  3. Compute ancestor-distance maps for both viewer and target. If target
 *     is in viewer's ancestor map → direct ancestor (depth = generationsUp).
 *     Likewise if viewer is in target's ancestor map → direct descendant.
 *  4. Find the closest common ancestor (CCA): the ancestor with the
 *     smallest combined (viewerDepth + targetDepth) that is in both maps.
 *     Let a = viewerDepth, b = targetDepth.
 *       - a===1 & b===1 → 'sibling'
 *       - a===1 & b>=2  → 'nibling' (niece/nephew chain), great^(b-2) prefix
 *       - a>=2 & b===1  → 'pibling' (aunt/uncle chain), great^(a-2) prefix
 *       - a===0 / b===0 already handled above (direct lineage)
 *       - a===b>=2     → cousin of degree (a-1), removal 0
 *       - a!==b, both>=2 → cousin of degree (min(a,b)-1), removal |a-b|
 *  5. No CCA found → fall back to a BFS that walks parent_ids in BOTH
 *     directions to find ANY path; if found, return 'extended' with the
 *     humanized path. If even that fails, return 'unknown'.
 */
/**
 * Hard cap on recursive `computeKinship` re-entries (in-law derivation
 * walks one spouse hop). 0 = top-level call. >=1 = inside an in-law
 * derivation — in that case we DO NOT recurse further, return 'unknown'
 * after the parent_ids walks. This cap is what eliminates the
 * "Maximum call stack size exceeded" crash for claimed child accounts
 * when two members point at each other via spouse_id with no parent_ids
 * path. Pure safety guard — never affects label correctness for any real
 * family graph (one spouse hop is always sufficient to derive an in-law).
 */
const KINSHIP_MAX_RECURSION_DEPTH = 4;

export const computeKinship = (
  members: GraphMember[],
  viewerId: string,
  targetId: string,
  __depth: number = 0,
): KinshipResult => {
  if (__depth > KINSHIP_MAX_RECURSION_DEPTH) {
    // eslint-disable-next-line no-console
    console.warn('[KinsCircle][kinship] computeKinship recursion cap hit', {
      viewerId,
      targetId,
      depth: __depth,
    });
    return { code: 'unknown', label: 'Unknown relation', generationsUp: 0, generationsDown: 0 };
  }
  if (viewerId === targetId) {
    return { code: 'self', label: 'You', generationsUp: 0, generationsDown: 0 };
  }
  const byId = new Map(members.map((m) => [m.id, m]));
  const viewer = byId.get(viewerId);
  const target = byId.get(targetId);
  if (!viewer || !target) {
    return { code: 'unknown', label: 'Unknown relation', generationsUp: 0, generationsDown: 0 };
  }


  // 1. Spouse / partner — RESTORED as an OPTIONAL display layer.
  //    Per the current task: spouse_id is checked AFTER direct lineage
  //    via parent_ids. Two members with a reciprocal spouse_id link are
  //    labeled as 'spouse' to each other ONLY when neither one is also
  //    a structural ancestor / descendant / sibling / cousin via the
  //    parent_ids walks below. In practice that "either-or" is enforced
  //    naturally by ordering: if there's a parent_ids path, sections 2-4
  //    return first and we never reach the spouse-only fallback. This
  //    means a co-parent who shares a child but isn't married still
  //    walks via parent_ids; the spouse label is reserved for an
  //    explicit partner relationship that has no bloodline path.
  //    parent_ids walks are PRIMARY — spouse_id never overrides them.
  if (
    (viewer.spouseId === target.id || target.spouseId === viewer.id)
  ) {
    // We will only RETURN spouse if no bloodline relationship exists.
    // Skip the early-return here so the parent_ids / ancestor / common-
    // ancestor walks below get a chance to produce their label first.
    // The fallback at the very bottom of this function checks the
    // spouse link again and returns 'spouse' only when nothing else
    // matched. This guarantees parent-child logic always wins.
  }



  // 2. Direct lineage (target in viewer's ancestor chain).
  const viewerAncestors = ancestorDistances(members, viewerId);
  if (viewerAncestors.has(target.id) && target.id !== viewerId) {
    const depth = viewerAncestors.get(target.id)!;
    if (depth === 1) {
      // Honor stored side as a hint for mother vs father.
      const lbl =
        target.relationship === 'mother' ? 'Mother' :
        target.relationship === 'father' ? 'Father' :
        target.relationship === 'step-parent' ? 'Step-Parent' :
        target.relationship === 'adopted-parent' ? 'Adopted Parent' :
        'Parent';
      return { code: 'parent', label: lbl, generationsUp: 1, generationsDown: 0 };
    }
    // depth 2 → Grandparent, depth 3 → Great-Grandparent, depth 4 → Great-Great-Grandparent, …
    const greatLevels = depth - 2;
    return {
      code: 'ancestor',
      label: `${greatPrefix(greatLevels)}Grandparent`,
      generationsUp: depth,
      generationsDown: 0,
    };
  }

  // 3. Direct lineage (viewer in target's ancestor chain → target is a descendant).
  const targetAncestors = ancestorDistances(members, targetId);
  if (targetAncestors.has(viewerId) && viewerId !== targetId) {
    const depth = targetAncestors.get(viewerId)!;
    if (depth === 1) {
      return { code: 'child', label: 'Child', generationsUp: 0, generationsDown: 1 };
    }
    const greatLevels = depth - 2;
    return {
      code: 'descendant',
      label: `${greatPrefix(greatLevels)}Grandchild`,
      generationsUp: 0,
      generationsDown: depth,
    };
  }

  // 4. Closest common ancestor.
  let ccaId: string | null = null;
  let bestSum = Number.POSITIVE_INFINITY;
  let bestA = 0;
  let bestB = 0;
  for (const [aid, a] of viewerAncestors) {
    if (aid === viewerId) continue;
    const b = targetAncestors.get(aid);
    if (b === undefined) continue;
    const sum = a + b;
    if (sum < bestSum) {
      bestSum = sum;
      ccaId = aid;
      bestA = a;
      bestB = b;
    }
  }

  if (ccaId !== null) {
    const a = bestA;
    const b = bestB;
    if (a === 1 && b === 1) {
      // Share at least one parent → sibling. Distinguish full vs half:
      //
      // Modern-family rule (Bug fix — "my mom and dad are no longer a
      // couple, the tree shouldn't force them into a couple"):
      //   - FULL sibling = every known parent on each side is shared
      //     with the other side.
      //   - HALF sibling = shares at least one parent but at least one
      //     side knows about an additional parent the other doesn't.
      //     Typical case: Viewer parent_ids = [Mom, Dad],
      //     Target parent_ids = [Mom] → half-sibling. Or Viewer =
      //     [Mom, Dad], Target = [Mom, StepDad] → also half-sibling.
      //
      // Pure derivation from parent_ids — no DB writes, no schema
      // change, no auto-spouse-coupling. Two parents who happen to
      // share a child stay structurally independent unless they have
      // an explicit spouse_id link.
      const viewerParents = new Set(
        (viewer.parentIds ?? []).filter((pid) => byId.has(pid)),
      );
      const targetParents = new Set(
        (target.parentIds ?? []).filter((pid) => byId.has(pid)),
      );
      let shared = 0;
      for (const pid of viewerParents) {
        if (targetParents.has(pid)) shared += 1;
      }
      const viewerAllShared =
        viewerParents.size > 0 && shared === viewerParents.size;
      const targetAllShared =
        targetParents.size > 0 && shared === targetParents.size;
      const isFull = viewerAllShared && targetAllShared;
      if (isFull) {
        return { code: 'sibling', label: 'Sibling', generationsUp: 0, generationsDown: 0 };
      }
      return { code: 'sibling', label: 'Half-Sibling', generationsUp: 0, generationsDown: 0 };
    }
    if (a === 1 && b >= 2) {
      // Viewer's parent is target's ancestor → target descends from viewer's
      // sibling. Niece / nephew (b===2) or great^(b-2)-niece/nephew.
      const greatLevels = b - 2;
      return {
        code: 'nibling',
        label: `${greatPrefix(greatLevels)}Niece / Nephew`,
        generationsUp: 0,
        generationsDown: b - 1,
      };
    }

    if (a >= 2 && b === 1) {
      // Target's parent is viewer's ancestor → target is sibling of viewer's
      // ancestor. Aunt/uncle (a===2) or great^(a-2)-aunt/uncle.
      const greatLevels = a - 2;
      return {
        code: 'pibling',
        label: `${greatPrefix(greatLevels)}Aunt / Uncle`,
        generationsUp: a - 1,
        generationsDown: 0,
      };
    }
    if (a >= 2 && b >= 2) {
      // Cousins. Degree = min(a, b) - 1. Removal = |a - b|.
      const degree = Math.min(a, b) - 1;
      const removal = Math.abs(a - b);
      const baseTitle = `${ordinal(degree)} cousin`;
      return {
        code: 'cousin',
        label: `${baseTitle}${removalPhrase(removal)}`,
        generationsUp: a,
        generationsDown: b,
        cousinDegree: degree,
        removal,
      };
    }
  }

  // 5. Safe fallback — try a bidirectional BFS over parent_ids (both
  //    directions) to find ANY path, then label it as "Extended relative"
  //    and surface the path.
  const childrenOf = new Map<string, string[]>();
  for (const m of members) {
    for (const pid of m.parentIds ?? []) {
      if (!childrenOf.has(pid)) childrenOf.set(pid, []);
      childrenOf.get(pid)!.push(m.id);
    }
  }
  // BFS from viewer outward through both parent and child links to find target.
  const prev = new Map<string, { from: string; step: 'up' | 'down' }>();
  const queue: string[] = [viewerId];
  const seen = new Set<string>([viewerId]);
  let found = false;
  while (queue.length) {
    const cur = queue.shift()!;
    if (cur === targetId) {
      found = true;
      break;
    }
    const m = byId.get(cur);
    if (!m) continue;
    // Up: parents.
    for (const pid of m.parentIds ?? []) {
      if (seen.has(pid) || !byId.has(pid)) continue;
      seen.add(pid);
      prev.set(pid, { from: cur, step: 'up' });
      queue.push(pid);
    }
    // Down: children.
    for (const cid of childrenOf.get(cur) ?? []) {
      if (seen.has(cid)) continue;
      seen.add(cid);
      prev.set(cid, { from: cur, step: 'down' });
      queue.push(cid);
    }
  }
  if (found) {
    // Reconstruct path viewer → … → target as humanized step labels.
    const reverse: Array<{ from: string; to: string; step: 'up' | 'down' }> = [];
    let cursor = targetId;
    while (cursor !== viewerId) {
      const p = prev.get(cursor);
      if (!p) break;
      reverse.push({ from: p.from, to: cursor, step: p.step });
      cursor = p.from;
    }
    reverse.reverse();
    const stepLabels: string[] = ['You'];
    for (const hop of reverse) {
      const targetName = byId.get(hop.to)?.name || 'Relative';
      const targetRel = byId.get(hop.to)?.relationship;
      let stepLbl: string;
      if (hop.step === 'up') {
        stepLbl =
          targetRel === 'mother' ? 'Mother' :
          targetRel === 'father' ? 'Father' :
          'Parent';
      } else {
        stepLbl = 'Child';
      }
      // Append name in parens to disambiguate when there are multiples.
      stepLabels.push(`${stepLbl} (${targetName})`);
    }
    return {
      code: 'extended',
      label: 'Extended relative',
      generationsUp: 0,
      generationsDown: 0,
      path: stepLabels,
    };
  }

  // ─── IN-LAW DERIVATION VIA ONE SPOUSE HOP (Modern-Family rule, May 2026) ──
  // Before falling back to 'unknown', try to derive an in-law label by
  // walking exactly ONE spouse_id hop on either side. This makes the
  // following labels appear automatically without ever storing them:
  //
  //   • Target is my child's spouse        → Son-in-law / Daughter-in-law
  //   • Target is my sibling's spouse      → Brother-in-law / Sister-in-law
  //   • Target is my parent's spouse       → (already handled by parent_ids
  //                                           when target IS the other
  //                                           parent; otherwise → Step-Parent)
  //   • Target is my spouse's parent       → Mother-in-law / Father-in-law
  //   • Target is my spouse's sibling      → Brother-in-law / Sister-in-law
  //   • Target is my spouse's child        → Step-Child
  //
  // Pure derivation — never mutates any data, never requires the in-law
  // to have parent_ids set. parent_ids walks above always win when they
  // produce a structural label, so this only fires for genuinely
  // marriage-derived relationships.
  const inLawSuffix = (gender: string | null | undefined, base: 'parent' | 'child' | 'sibling'): string => {
    const g = (gender ?? '').toLowerCase();
    if (base === 'parent') {
      if (g === 'female') return 'Mother-in-law';
      if (g === 'male') return 'Father-in-law';
      return 'Parent-in-law';
    }
    if (base === 'child') {
      if (g === 'female') return 'Daughter-in-law';
      if (g === 'male') return 'Son-in-law';
      return 'Child-in-law';
    }
    if (g === 'female') return 'Sister-in-law';
    if (g === 'male') return 'Brother-in-law';
    return 'Sibling-in-law';
  };
  const targetGender = (target as any).gender as string | null | undefined;

  // Strategy 1 — target's spouse is a structural relative of the viewer.
  // (e.g. target married my child → target is my son/daughter-in-law)
  if (target.spouseId) {
    const partner = byId.get(target.spouseId);
    if (partner && partner.id !== target.id && partner.id !== viewerId) {
      const innerK = computeKinship(members, viewerId, partner.id, __depth + 1);

      if (innerK.code === 'child') {
        return { code: 'extended', label: inLawSuffix(targetGender, 'child'), generationsUp: 0, generationsDown: 1 };
      }
      if (innerK.code === 'sibling') {
        return { code: 'extended', label: inLawSuffix(targetGender, 'sibling'), generationsUp: 0, generationsDown: 0 };
      }
      if (innerK.code === 'parent') {
        // My parent's spouse who isn't already my parent → Step-Parent.
        return { code: 'extended', label: 'Step-Parent', generationsUp: 1, generationsDown: 0 };
      }
      if (innerK.code === 'descendant') {
        return { code: 'extended', label: `${innerK.label}-in-law`, generationsUp: 0, generationsDown: innerK.generationsDown };
      }
      if (innerK.code === 'pibling') {
        return { code: 'extended', label: `${innerK.label}-in-law`, generationsUp: innerK.generationsUp, generationsDown: 0 };
      }
      if (innerK.code === 'nibling') {
        return { code: 'extended', label: `${innerK.label}-in-law`, generationsUp: 0, generationsDown: innerK.generationsDown };
      }
      if (innerK.code === 'cousin') {
        return { code: 'extended', label: `${innerK.label} by marriage`, generationsUp: innerK.generationsUp, generationsDown: innerK.generationsDown, cousinDegree: innerK.cousinDegree, removal: innerK.removal };
      }
      if (innerK.code === 'ancestor') {
        return { code: 'extended', label: `${innerK.label}-in-law`, generationsUp: innerK.generationsUp, generationsDown: 0 };
      }
    }
  }
  // Reverse: someone else stored spouse_id pointing at target.
  for (const m of members) {
    if (m.id === target.id || m.id === viewerId) continue;
    if (m.spouseId !== target.id) continue;
    const innerK = computeKinship(members, viewerId, m.id, __depth + 1);

    if (innerK.code === 'child') {
      return { code: 'extended', label: inLawSuffix(targetGender, 'child'), generationsUp: 0, generationsDown: 1 };
    }
    if (innerK.code === 'sibling') {
      return { code: 'extended', label: inLawSuffix(targetGender, 'sibling'), generationsUp: 0, generationsDown: 0 };
    }
    if (innerK.code === 'parent') {
      return { code: 'extended', label: 'Step-Parent', generationsUp: 1, generationsDown: 0 };
    }
    if (innerK.code === 'descendant' || innerK.code === 'pibling' || innerK.code === 'nibling' || innerK.code === 'ancestor') {
      return { code: 'extended', label: `${innerK.label}-in-law`, generationsUp: innerK.generationsUp, generationsDown: innerK.generationsDown };
    }
    if (innerK.code === 'cousin') {
      return { code: 'extended', label: `${innerK.label} by marriage`, generationsUp: innerK.generationsUp, generationsDown: innerK.generationsDown, cousinDegree: innerK.cousinDegree, removal: innerK.removal };
    }
    break;
  }

  // Strategy 2 — viewer's spouse is a structural relative of the target.
  // (e.g. target is my spouse's parent → target is my Mother/Father-in-law)
  if (viewer.spouseId) {
    const myPartner = byId.get(viewer.spouseId);
    if (myPartner && myPartner.id !== viewerId && myPartner.id !== target.id) {
      const innerK = computeKinship(members, myPartner.id, target.id, __depth + 1);

      if (innerK.code === 'parent') {
        return { code: 'extended', label: inLawSuffix(targetGender, 'parent'), generationsUp: 1, generationsDown: 0 };
      }
      if (innerK.code === 'sibling') {
        return { code: 'extended', label: inLawSuffix(targetGender, 'sibling'), generationsUp: 0, generationsDown: 0 };
      }
      if (innerK.code === 'child') {
        return { code: 'extended', label: 'Step-Child', generationsUp: 0, generationsDown: 1 };
      }
      if (innerK.code === 'ancestor') {
        return { code: 'extended', label: `${innerK.label}-in-law`, generationsUp: innerK.generationsUp, generationsDown: 0 };
      }
      if (innerK.code === 'pibling') {
        return { code: 'extended', label: `${innerK.label}-in-law`, generationsUp: innerK.generationsUp, generationsDown: 0 };
      }
    }
  }

  // Final fallback — if there's a reciprocal spouse_id link and no
  // bloodline / in-law path was found, return 'spouse'. This is the
  // OPTIONAL display layer: it only fires after every parent_ids walk
  // and every in-law derivation failed, which guarantees that bloodline
  // / parent-child relationships always win over an explicit spouse
  // pairing.
  if (viewer.spouseId === target.id || target.spouseId === viewer.id) {
    return {
      code: 'spouse',
      label: 'Spouse / Partner',
      generationsUp: 0,
      generationsDown: 0,
    };
  }

  return {
    code: 'unknown',
    label: 'Unknown relation',
    generationsUp: 0,
    generationsDown: 0,
  };
};



/**
 * Human-readable kinship label with full unlimited-generation support.
 *
 * VIEWER-PERSPECTIVE PRIORITY (Bug A fix):
 *   The kinship engine is the source of truth for any relationship that
 *   can be structurally derived from `parent_ids` + `spouse_id`. The
 *   per-row `relationshipOverride` is treated as a FALLBACK, only used
 *   when:
 *     (a) the engine returns 'unknown' / 'extended' (i.e. it can't
 *         classify the connection), OR
 *     (b) the viewer is unknown.
 *
 *   Why: an override saved by the original creator (e.g. Marion writing
 *   "Grandchild" on Arkinya's row) is a label from THEIR viewpoint — it
 *   is NOT the truth for every other viewer. When Chekesha (Arkinya's
 *   parent) views the same row, the engine derives "Child" from
 *   parent_ids and that perspective-correct label MUST win. The override
 *   is still honored for special cases the algorithm can't infer
 *   ("Godmother", "Mother-in-law", "Family Friend"), because in those
 *   cases the engine simply returns 'unknown' / 'extended' and the
 *   override fills the gap.
 *
 *   parent_ids and spouse_id remain the single source of structural
 *   truth; this function only changes how a label is DISPLAYED.
 *
 * @param member  The relative being labeled.
 * @param viewerId The currently logged-in user's claimed member id (or null).
 * @param members Full member list of the loaded family graph.
 * @returns A perspective-correct display string for the given viewer.
 */
export const kinshipLabel = (
  member: GraphMember,
  viewerId: string | null,
  members: GraphMember[]
): string => {
  const override = (member.relationshipOverride ?? '').trim();

  // Extended-generation labels selected by the user must display exactly.
  // Example: Larry connected through Mae Ruth as Great-Great-Aunt/Uncle
  // should not be collapsed to Aunt/Uncle by the structural fallback.
  const extendedOverride = /^(great-|great\s|grandchild|great-grandchild|niece|nephew|cousin)/i.test(override);
  if (extendedOverride) return override;

  if (!viewerId) {
    // Unknown viewer — engine has no anchor to compute from. Prefer the
    // stored override (if any), otherwise the stored relationship enum.
    if (override) return override;
    return RELATIONSHIP_OPTIONS.find((o) => o.value === member.relationship)?.label || 'Relative';
  }
  if (member.id === viewerId) return 'You';
  // Engine FIRST — derives the label from parent_ids / spouse_id walks
  // RELATIVE TO THE CURRENT VIEWER. This is the perspective-correct
  // answer for any structurally-provable relationship.
  const k = computeKinship(members, viewerId, member.id);
  if (k.code !== 'unknown' && k.code !== 'extended') {
    return k.label;
  }
  // Engine couldn't classify (no parent_ids path, no spouse link). Fall
  // back to the override if one was saved — this is where "Godmother",
  // "Mother-in-law", "Family Friend" labels come back into the UI.
  if (override) return override;
  return k.label;
};


/**
 * Convenience accessor — returns the kinship path ("You → Mother → Sister
 * → Son") for the safe-fallback "Extended relative" rendering. Returns null
 * for non-extended relations so callers can decide whether to display it.
 */
export const kinshipPath = (
  member: GraphMember,
  viewerId: string | null,
  members: GraphMember[]
): string[] | null => {
  if (!viewerId || member.id === viewerId) return null;
  const k = computeKinship(members, viewerId, member.id);
  if (k.code !== 'extended') return null;
  return k.path ?? null;
};

/**
 * Pretty relationship label from the perspective of a viewer. Used for
 * roster + drawer headers when we want to show the verb-form ("Aunt",
 * "Cousin", "Grandfather") instead of the raw enum value.
 *
 * Now backed by the generational kinship engine so it supports unlimited
 * "great-" depth, cousin degrees / removals, and per-member admin overrides.
 * Old call-sites continue to work unchanged.
 */
export const viewerRelationshipLabel = (
  member: GraphMember,
  viewerId: string | null,
  members: GraphMember[]
): string => kinshipLabel(member, viewerId, members);
